import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { fErr, fieldCls, clearErr, extractServerErrors, required } from "@/lib/form-validation";
import {
  CheckCircle2, XCircle, AlertTriangle, Clock, Plus, Trash2, ShieldCheck,
  ClipboardCheck, Play, RefreshCw, Bot,
} from "lucide-react";

// ── Auth ──────────────────────────────────────────────────────────────────────
function useAuthRegression() {
  const token =
    (typeof window !== "undefined" &&
      (localStorage.getItem("dotnetbot.jwt") ||
        sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface RegressionControl {
  id: string;
  organizationId: string;
  applicationName: string;
  releaseVersion: string | null;
  environment: string;
  testingRequired: boolean;
  regressionTestingEnabled: boolean;
  automatedTestingEnabled: boolean;
  manualTestingRequired: boolean;
  lastTestDate: string | null;
  nextTestDate: string | null;
  testingTool: string | null;
  testEvidenceReference: string | null;
  findingsDocumentReference: string | null;
  findingsExist: boolean;
  testerName: string | null;
  approvalStatus: string;
  complianceStatus: string;
  riskLevel: string;
  status: string;
  remediationNotes: string | null;
  lastReviewedBy: string | null;
  reviewNotes: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  createdAt: string;
  updatedAt: string;
  reviewOverdue: boolean;
  validationCodes: string[];
}

interface RegressionSummary {
  totalApplications: number;
  compliantApplications: number;
  approvedApplications: number;
  withFindings: number;
  pendingApproval: number;
  reviewOverdue: number;
}

const CODE_LABELS: Record<string, string> = {
  REGRESSION_TESTING_DISABLED:    "Regression testing disabled",
  AUTOMATED_TESTING_REQUIRED:     "Automated testing not configured",
  MANUAL_TESTING_REQUIRED:        "Manual testing not required",
  TEST_EVIDENCE_MISSING:          "Test evidence missing",
  TEST_DATE_MISSING:              "No test date recorded",
  FINDINGS_DOCUMENTATION_REQUIRED:"Findings not documented",
  APPROVAL_REQUIRED:              "Approval pending",
  REVIEW_OVERDUE:                 "Review overdue",
};

const BASE = "/api/organization/compliance/wcag/accessibility-regression";

function fmt(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function RiskBadge({ level }: { level: string }) {
  const cls =
    level === "Critical" ? "bg-red-100 text-red-700 border-red-200" :
    level === "High"     ? "bg-orange-100 text-orange-700 border-orange-200" :
    level === "Medium"   ? "bg-amber-100 text-amber-700 border-amber-200" :
                           "bg-blue-100 text-blue-700 border-blue-200";
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${cls}`}>{level}</span>;
}

function CompBadge({ status }: { status: string }) {
  if (status === "Compliant")
    return <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-emerald-100 text-emerald-700 border-emerald-200">Compliant</span>;
  if (status === "NonCompliant")
    return <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-red-100 text-red-700 border-red-200">Non-Compliant</span>;
  return <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200">{status}</span>;
}

function ApprovalBadge({ status }: { status: string }) {
  if (status === "Approved")
    return <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-emerald-100 text-emerald-700 border-emerald-200">Approved</span>;
  if (status === "Rejected")
    return <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-red-100 text-red-700 border-red-200">Rejected</span>;
  return <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-amber-100 text-amber-700 border-amber-200">{status}</span>;
}

function EnvBadge({ env }: { env: string }) {
  const cls =
    env === "Production" ? "bg-red-100 text-red-700 border-red-200" :
    env === "Staging"    ? "bg-amber-100 text-amber-700 border-amber-200" :
    env === "All"        ? "bg-purple-100 text-purple-700 border-purple-200" :
                           "bg-blue-100 text-blue-700 border-blue-200";
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${cls}`}>{env}</span>;
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ sub, canManage, onAdd }: { sub?: string; canManage: boolean; onAdd?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
      <ShieldCheck className="w-10 h-10 text-muted-foreground/40" aria-hidden="true" />
      <p className="text-sm font-medium text-muted-foreground">No records found</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      {canManage && onAdd && (
        <Button size="sm" variant="outline" onClick={onAdd}>
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Control
        </Button>
      )}
    </div>
  );
}

// ── Form validation helpers ───────────────────────────────────────────────────
function FErr({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <p role="alert" className="text-xs text-red-500 mt-0.5 flex items-center gap-1"><span aria-hidden="true">⚠</span> {msg}</p>;
}
function apiErrMsg(status: number): string {
  if (status === 400) return "Please correct the highlighted fields.";
  if (status === 401) return "Your session has expired. Please log in again.";
  if (status === 403) return "You do not have permission to perform this action.";
  if (status === 404) return "The requested record was not found.";
  if (status >= 500) return "Something went wrong while saving. Please try again.";
  return "Please correct the highlighted fields.";
}

// ── Add / Edit Dialog ─────────────────────────────────────────────────────────
function AddEditDialog({
  editing, canManage, onClose, onSaved,
}: {
  editing: RegressionControl | null;
  canManage: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { header } = useAuthRegression();
  const { toast } = useToast();
  const [form, setForm] = useState({
    applicationName:         editing?.applicationName         ?? "",
    releaseVersion:          editing?.releaseVersion          ?? "",
    environment:             editing?.environment             ?? "Production",
    testingRequired:         editing?.testingRequired         ?? false,
    regressionTestingEnabled:editing?.regressionTestingEnabled?? false,
    automatedTestingEnabled: editing?.automatedTestingEnabled ?? false,
    manualTestingRequired:   editing?.manualTestingRequired   ?? false,
    testingTool:             editing?.testingTool             ?? "",
    testEvidenceReference:   editing?.testEvidenceReference   ?? "",
    findingsDocumentReference:editing?.findingsDocumentReference ?? "",
    findingsExist:           editing?.findingsExist           ?? false,
    testerName:              editing?.testerName              ?? "",
    approvalStatus:          editing?.approvalStatus          ?? "Pending",
    riskLevel:               editing?.riskLevel               ?? "High",
    status:                  editing?.status                  ?? "Draft",
    remediationNotes:        editing?.remediationNotes        ?? "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: async () => {
      const body = {
        applicationName:          form.applicationName.trim(),
        releaseVersion:           form.releaseVersion.trim() || null,
        environment:              form.environment,
        testingRequired:          form.testingRequired,
        regressionTestingEnabled: form.regressionTestingEnabled,
        automatedTestingEnabled:  form.automatedTestingEnabled,
        manualTestingRequired:    form.manualTestingRequired,
        testingTool:              form.testingTool.trim() || null,
        testEvidenceReference:    form.testEvidenceReference.trim() || null,
        findingsDocumentReference:form.findingsDocumentReference.trim() || null,
        findingsExist:            form.findingsExist,
        testerName:               form.testerName.trim() || null,
        approvalStatus:           form.approvalStatus,
        riskLevel:                form.riskLevel,
        status:                   form.status,
        remediationNotes:         form.remediationNotes.trim() || null,
      };
      const res = editing
        ? await fetch(`${BASE}/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) })
        : await fetch(BASE, { method: "POST", headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw Object.assign(new Error(apiErrMsg(res.status)), b); }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: editing ? "Changes saved successfully." : "Control created successfully." });
      onSaved();
      onClose();
    },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length) {
        setErrors(serverErrs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
    },
  });

  const toggle = (key: keyof typeof form) =>
    setForm(f => ({ ...f, [key]: !f[key as keyof typeof form] }));
  const set = (key: keyof typeof form, val: string) =>
    setForm(f => ({ ...f, [key]: val }));

  return (
    <Dialog open onOpenChange={o => { if (!o) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit" : "Add"} Accessibility Regression Control</DialogTitle>
          <DialogDescription>Metadata only — no scan output or user data stored. No scan output or user data is stored.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Application Info */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Application Info</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Application Name *</Label>
                <Input
                  className={fieldCls("", errors, "applicationName")}
                  value={form.applicationName}
                  onChange={e => { set("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }}
                  aria-invalid={!!errors.applicationName}
                  placeholder="Student Portal"
                />
                {fErr(errors, "applicationName")}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Release Version</Label>
                <Input value={form.releaseVersion} onChange={e => set("releaseVersion", e.target.value)} placeholder="v2.4.1" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Environment</Label>
                <select value={form.environment} onChange={e => set("environment", e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  {["Development", "Staging", "Production", "All"].map(e =>
                    <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Risk Level</Label>
                <select value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  {["Low", "Medium", "High", "Critical"].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <select value={form.status} onChange={e => set("status", e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  {["Draft", "Active", "UnderReview", "Approved", "Archived"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Testing Configuration */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Testing Configuration</p>
            <div className="space-y-2">
              {([
                ["testingRequired",          "Accessibility Testing Required"],
                ["regressionTestingEnabled", "Regression Testing Enabled"],
                ["automatedTestingEnabled",  "Automated Testing Enabled"],
                ["manualTestingRequired",    "Manual Testing Required"],
                ["findingsExist",            "Findings Exist (requires documentation)"],
              ] as [keyof typeof form, string][]).map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 border rounded-md p-2.5 cursor-pointer hover:bg-muted/30">
                  <input type="checkbox" checked={!!form[key]} onChange={() => toggle(key)} className="h-4 w-4 rounded" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Testing Details */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Testing Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Testing Tool</Label>
                <Input value={form.testingTool} onChange={e => set("testingTool", e.target.value)} placeholder="axe-core, Pa11y, Deque…" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tester Name</Label>
                <Input value={form.testerName} onChange={e => set("testerName", e.target.value)} placeholder="Jane Smith" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Test Evidence Reference</Label>
                <Input value={form.testEvidenceReference} onChange={e => set("testEvidenceReference", e.target.value)} placeholder="evidence://wcag-test-2026.pdf" />
              </div>
              {form.findingsExist && (
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Findings Document Reference *</Label>
                  <Input value={form.findingsDocumentReference} onChange={e => set("findingsDocumentReference", e.target.value)} placeholder="findings://report-2026.pdf" />
                </div>
              )}
            </div>
          </div>

          {/* Approval */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Approval</p>
            <div className="space-y-1">
              <Label className="text-xs">Approval Status</Label>
              <select value={form.approvalStatus} onChange={e => set("approvalStatus", e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                {["Pending", "UnderReview", "Approved", "Rejected"].map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>

          {/* Remediation */}
          {(form.remediationNotes || editing) && (
            <div className="space-y-1">
              <Label className="text-xs">Remediation Notes</Label>
              <Textarea value={form.remediationNotes} onChange={e => set("remediationNotes", e.target.value)} rows={3} placeholder="Document remediation actions…" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "applicationName", form.applicationName, "Application Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); save.mutate(); }} disabled={save.isPending}>
            {save.isPending ? "Saving…" : editing ? "Save Changes" : "Add Control"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validate Dialog ───────────────────────────────────────────────────────────
function ValidateDialog({
  control, onClose, onDone,
}: {
  control: RegressionControl;
  onClose: () => void;
  onDone: () => void;
}) {
  const { header } = useAuthRegression();
  const { toast } = useToast();
  const [result, setResult] = useState<any>(null);

  const validate = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/${control.id}/validate`, { method: "POST", headers: { ...header } });
      if (!res.ok) throw new Error(apiErrMsg(res.status));
      return res.json();
    },
    onSuccess: (data) => { setResult(data); onDone(); },
    onError: (e: any) => toast({ title: "Validation failed", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Validate — {control.applicationName}</DialogTitle>
          <DialogDescription>Runs all 8 validation checks against this control record.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Config summary */}
          <div className="border rounded-md p-3 bg-muted/20 grid grid-cols-2 gap-1.5 text-xs">
            {[
              ["Regression Testing", control.regressionTestingEnabled],
              ["Automated Testing",  control.automatedTestingEnabled],
              ["Manual Testing",     control.manualTestingRequired],
              ["Test Evidence",      !!control.testEvidenceReference],
              ["Test Date",          !!control.lastTestDate],
              ["Approval Status",    control.approvalStatus === "Approved"],
            ].map(([label, ok]) => (
              <div key={String(label)} className="flex items-center gap-1.5">
                {ok
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" aria-hidden="true" />
                  : <XCircle className="w-3.5 h-3.5 text-red-500" aria-hidden="true" />}
                <span className={ok ? "text-foreground" : "text-red-600"}>{String(label)}</span>
              </div>
            ))}
          </div>

          {result && (
            <div className="space-y-3">
              <div className={`border rounded-md p-3 text-sm font-medium flex items-center gap-2 ${result.isCompliant ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
                {result.isCompliant
                  ? <><CheckCircle2 className="w-4 h-4" aria-hidden="true" /> All checks passed — Compliant</>
                  : <><AlertTriangle className="w-4 h-4" aria-hidden="true" /> {result.validationCodes.length} issue{result.validationCodes.length !== 1 ? "s" : ""} found</>}
              </div>
              {result.validationCodes.length > 0 && (
                <div className="space-y-2">
                  {result.validationCodes.map((code: string) => (
                    <div key={code} className="border rounded-md p-2.5 bg-background text-xs space-y-1">
                      <p className="font-medium text-red-700">{code}</p>
                      <p className="text-muted-foreground">{result.remediationSuggestions?.[code] ?? CODE_LABELS[code] ?? code}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => validate.mutate()} disabled={validate.isPending}>
            {validate.isPending ? "Validating…" : result ? "Re-validate" : "Run Validation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Run Test Dialog ───────────────────────────────────────────────────────────
const TEST_PRESETS = [
  { label: "All clear",            testType: "Automated", toolName: "axe-core",  testerName: "" },
  { label: "Manual screen-reader", testType: "Manual",    toolName: "NVDA",      testerName: "" },
  { label: "Pa11y CI run",         testType: "Automated", toolName: "Pa11y",     testerName: "" },
  { label: "Full release check",   testType: "Combined",  toolName: "axe + NVDA",testerName: "" },
];

function RunTestDialog({
  control, onClose, onDone,
}: {
  control: RegressionControl;
  onClose: () => void;
  onDone: () => void;
}) {
  const { header } = useAuthRegression();
  const { toast } = useToast();
  const [form, setForm] = useState({ testType: "Automated", toolName: "", testerName: "", evidenceReference: "" });
  const [result, setResult] = useState<any>(null);

  const run = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/${control.id}/run-test`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ testType: form.testType, toolName: form.toolName || null, testerName: form.testerName || null, evidenceReference: form.evidenceReference || null }),
      });
      if (!res.ok) throw new Error(apiErrMsg(res.status));
      return res.json();
    },
    onSuccess: (data) => { setResult(data); onDone(); },
    onError: (e: any) => toast({ title: "Unable to complete test run", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Run Accessibility Test — {control.applicationName}</DialogTitle>
          <DialogDescription>
            <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
              <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" /> Simulation only
            </span>
            {" "}— records metadata from your actual release pipeline. No real scanner is executed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Presets */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Quick Presets</p>
            <div className="grid grid-cols-2 gap-2">
              {TEST_PRESETS.map(p => (
                <button key={p.label} type="button"
                  className="border rounded-md p-2 text-xs text-left hover:bg-muted/40 transition-colors"
                  onClick={() => setForm(f => ({ ...f, testType: p.testType, toolName: p.toolName }))}>
                  <p className="font-medium">{p.label}</p>
                  <p className="text-muted-foreground">{p.testType} · {p.toolName}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Test Type</Label>
              <select value={form.testType} onChange={e => setForm(f => ({ ...f, testType: e.target.value }))}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                {["Automated", "Manual", "Combined", "Regression", "Smoke"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tool Name</Label>
              <Input value={form.toolName} onChange={e => setForm(f => ({ ...f, toolName: e.target.value }))} placeholder="axe-core, Pa11y…" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tester Name</Label>
              <Input value={form.testerName} onChange={e => setForm(f => ({ ...f, testerName: e.target.value }))} placeholder="Jane Smith" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Evidence Reference</Label>
              <Input value={form.evidenceReference} onChange={e => setForm(f => ({ ...f, evidenceReference: e.target.value }))} placeholder="ci://build-1234" />
            </div>
          </div>

          {result && (
            <div className="space-y-3 border-t pt-3">
              <div className={`border rounded-md p-3 text-sm font-medium flex items-center gap-2 ${result.passed ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                {result.passed
                  ? <><CheckCircle2 className="w-4 h-4" aria-hidden="true" /> Test simulation passed</>
                  : <><AlertTriangle className="w-4 h-4" aria-hidden="true" /> {result.issues.length} testing gap{result.issues.length !== 1 ? "s" : ""} detected</>}
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                {[
                  ["Regression", result.regressionTestingEnabled],
                  ["Automated",  result.automatedTestingEnabled],
                  ["Manual",     result.manualTestingRequired],
                ].map(([label, ok]) => (
                  <div key={String(label)} className="border rounded-md p-2 flex flex-col items-center gap-1">
                    {ok
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-500" aria-hidden="true" />
                      : <XCircle className="w-4 h-4 text-red-500" aria-hidden="true" />}
                    <span className="font-medium">{String(label)}</span>
                  </div>
                ))}
              </div>
              {result.issues.length > 0 && (
                <div className="space-y-1">
                  {result.issues.map((code: string) => (
                    <div key={code} className="text-xs border rounded px-2 py-1.5 bg-amber-50 text-amber-700">
                      {CODE_LABELS[code] ?? code}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-muted-foreground italic">{result.note}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => run.mutate()} disabled={run.isPending}>
            <Play className="w-3.5 h-3.5 mr-1.5" />
            {run.isPending ? "Running…" : result ? "Run Again" : "Run Simulation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review Dialog ─────────────────────────────────────────────────────────────
function ReviewDialog({
  control, onClose, onDone,
}: {
  control: RegressionControl;
  onClose: () => void;
  onDone: () => void;
}) {
  const { header } = useAuthRegression();
  const { toast } = useToast();
  const [form, setForm] = useState({ reviewerName: "", reviewNotes: "", approvalStatus: control.approvalStatus, nextReviewDate: "" });
  const [reviewerErr, setReviewerErr] = useState("");

  const submit = useMutation({
    mutationFn: async () => {
      if (!form.reviewerName.trim()) { setReviewerErr("Reviewer name is required."); throw new Error("Please fix the highlighted fields."); }
      setReviewerErr("");
      const body: any = { reviewerName: form.reviewerName.trim(), reviewNotes: form.reviewNotes.trim() || null, approvalStatus: form.approvalStatus };
      if (form.nextReviewDate) body.nextReviewDate = new Date(form.nextReviewDate).toISOString();
      const res = await fetch(`${BASE}/${control.id}/review`, { method: "POST", headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(apiErrMsg(res.status));
      return res.json();
    },
    onSuccess: () => { toast({ title: "Review completed successfully." }); onDone(); onClose(); },
    onError: (e: any) => toast({ title: "Unable to save review", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Review — {control.applicationName}</DialogTitle>
          <DialogDescription>Record the accessibility testing review outcome and approval decision.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="text-xs">Reviewer Name *</Label>
            <Input value={form.reviewerName} onChange={e => { setForm(f => ({ ...f, reviewerName: e.target.value })); if (e.target.value.trim()) setReviewerErr(""); }}
              placeholder="Jane Smith"
              className={reviewerErr ? "border-red-500 focus-visible:ring-red-500" : ""}
              aria-invalid={!!reviewerErr} />
            <FErr msg={reviewerErr} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Approval Decision</Label>
            <select value={form.approvalStatus} onChange={e => setForm(f => ({ ...f, approvalStatus: e.target.value }))}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
              {["Pending", "UnderReview", "Approved", "Rejected"].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Next Review Date</Label>
            <Input type="date" value={form.nextReviewDate} onChange={e => setForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Review Notes</Label>
            <Textarea value={form.reviewNotes} onChange={e => setForm(f => ({ ...f, reviewNotes: e.target.value }))} rows={3} placeholder="Observations, conditions, follow-up items…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => submit.mutate()} disabled={submit.isPending}>
            {submit.isPending ? "Saving…" : "Complete Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete Confirm ────────────────────────────────────────────────────────────
function DeleteDialog({ control, onClose, onDone }: { control: RegressionControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthRegression();
  const { toast } = useToast();
  const del = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/${control.id}`, { method: "DELETE", headers: { ...header } });
      if (!res.ok) throw new Error(apiErrMsg(res.status));
    },
    onSuccess: () => { toast({ title: "Control deleted successfully." }); onDone(); onClose(); },
    onError: (e: any) => toast({ title: "Unable to delete", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete Control</DialogTitle>
          <DialogDescription>Delete the accessibility regression control for <strong>{control.applicationName}</strong>? This cannot be undone.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => del.mutate()} disabled={del.isPending}>
            {del.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Section ──────────────────────────────────────────────────────────────
export function AccessibilityRegressionSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuthRegression();
  const QKEY = [BASE];

  const [addOpen, setAddOpen]           = useState(false);
  const [editing, setEditing]           = useState<RegressionControl | null>(null);
  const [validating, setValidating]     = useState<RegressionControl | null>(null);
  const [running, setRunning]           = useState<RegressionControl | null>(null);
  const [reviewing, setReviewing]       = useState<RegressionControl | null>(null);
  const [deleting, setDeleting]         = useState<RegressionControl | null>(null);

  // Filters
  const [filterApp, setFilterApp]         = useState("");
  const [filterEnv, setFilterEnv]         = useState("");
  const [filterApproval, setFilterApproval] = useState("");
  const [filterCompliance, setFilterCompliance] = useState("");
  const [filterRisk, setFilterRisk]       = useState("");

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [BASE + "/summary"] });
    queryClient.invalidateQueries({ queryKey: QKEY });
    queryClient.invalidateQueries({ queryKey: [BASE + "/issues"] });
  };

  const summaryQ = useQuery<RegressionSummary>({
    queryKey: [BASE + "/summary"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/summary`, { headers: { ...header } });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ total: number; items: any[] }>({
    queryKey: [BASE + "/issues"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/issues`, { headers: { ...header } });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const listQ = useQuery<RegressionControl[]>({
    queryKey: QKEY,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterApp)        params.set("applicationName", filterApp);
      if (filterEnv)        params.set("environment",      filterEnv);
      if (filterApproval)   params.set("approvalStatus",   filterApproval);
      if (filterCompliance) params.set("complianceStatus", filterCompliance);
      if (filterRisk)       params.set("riskLevel",        filterRisk);
      const res = await fetch(`${BASE}?${params}`, { headers: { ...header } });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const items = listQ.data ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4" aria-hidden="true" />
              Accessibility Regression Testing
            </CardTitle>
            <CardDescription className="mt-1 text-xs">
              Track accessibility regression testing processes and evidence in your release pipeline. Metadata only — no scan output or user data stored.
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary metrics */}
        {s && (
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: "Total Apps",     value: s.totalApplications,    color: "" },
              { label: "Compliant",      value: s.compliantApplications, color: s.compliantApplications === s.totalApplications && s.totalApplications > 0 ? "text-emerald-600" : "" },
              { label: "Approved",       value: s.approvedApplications,  color: s.approvedApplications  === s.totalApplications && s.totalApplications > 0 ? "text-emerald-600" : "" },
              { label: "With Findings",  value: s.withFindings,          color: s.withFindings > 0 ? "text-amber-600" : "" },
              { label: "Pending Approval",value: s.pendingApproval,      color: s.pendingApproval > 0 ? "text-amber-600" : "" },
              { label: "Review Overdue", value: s.reviewOverdue,         color: s.reviewOverdue > 0 ? "text-red-600" : "" },
            ].map(m => (
              <div key={m.label} className="border rounded-md p-2.5 text-center">
                <p className={`text-xl font-bold ${m.color || "text-foreground"}`}>{m.value}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{m.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Issues panel */}
        {issuesQ.data && issuesQ.data.total > 0 && (
          <div className="border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 rounded-md p-3 space-y-2">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
              {issuesQ.data.total} application{issuesQ.data.total !== 1 ? "s" : ""} with validation issues
            </p>
            {issuesQ.data.items.slice(0, 5).map((item: any) => (
              <div key={item.control.id} className="bg-background rounded border border-amber-200 dark:border-amber-700 p-2 text-xs">
                <p className="font-medium">{item.control.applicationName}
                  {item.control.releaseVersion && <span className="text-muted-foreground font-normal ml-1">({item.control.releaseVersion})</span>}
                </p>
                <p className="text-muted-foreground mt-0.5">{item.codes.slice(0, 2).map((c: string) => CODE_LABELS[c] ?? c).join(" · ")}{item.codes.length > 2 ? ` +${item.codes.length - 2} more` : ""}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Input placeholder="Filter by app name…" value={filterApp} onChange={e => { setFilterApp(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY }); }} className="text-xs h-8" />
          <select value={filterEnv} onChange={e => { setFilterEnv(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY }); }}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs">
            <option value="">All Environments</option>
            {["Development", "Staging", "Production", "All"].map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <select value={filterApproval} onChange={e => { setFilterApproval(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY }); }}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs">
            <option value="">All Approval Statuses</option>
            {["Pending", "UnderReview", "Approved", "Rejected"].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={filterCompliance} onChange={e => { setFilterCompliance(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY }); }}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs">
            <option value="">All Compliance</option>
            {["Compliant", "NonCompliant", "Pending"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterRisk} onChange={e => { setFilterRisk(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY }); }}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs">
            <option value="">All Risk Levels</option>
            {["Low", "Medium", "High", "Critical"].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={invalidate}>
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>

        {/* Control list */}
        {listQ.isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState
            sub="No accessibility regression controls match your filters."
            canManage={canManage}
            onAdd={canManage ? () => setAddOpen(true) : undefined}
          />
        ) : (
          <div className="space-y-2">
            {items.map(c => (
              <div key={c.id} className="border rounded-md p-3 space-y-2 hover:bg-muted/20 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-medium truncate">{c.applicationName}</span>
                      {c.releaseVersion && <span className="text-xs text-muted-foreground">({c.releaseVersion})</span>}
                      <EnvBadge env={c.environment} />
                      <RiskBadge level={c.riskLevel} />
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <CompBadge status={c.complianceStatus} />
                      <ApprovalBadge status={c.approvalStatus} />
                      {c.findingsExist && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-orange-100 text-orange-700 border-orange-200">Findings</span>
                      )}
                      {c.reviewOverdue && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-red-100 text-red-700 border-red-200 flex items-center gap-0.5">
                          <Clock className="w-3 h-3" aria-hidden="true" /> Review Overdue
                        </span>
                      )}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setRunning(c)} title="Run test simulation">
                        <Play className="w-3.5 h-3.5 mr-1" /> Test
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setValidating(c)} title="Validate">
                        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Validate
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(c)} title="Edit">Edit</Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setDeleting(c)} title="Delete">
                        <Trash2 className="w-3.5 h-3.5 text-destructive" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Testing config row */}
                <div className="grid grid-cols-3 gap-1.5 text-[11px]">
                  {[
                    ["Regression", c.regressionTestingEnabled],
                    ["Automated",  c.automatedTestingEnabled],
                    ["Manual",     c.manualTestingRequired],
                  ].map(([label, ok]) => (
                    <div key={String(label)} className="flex items-center gap-1 text-muted-foreground">
                      {ok
                        ? <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" aria-hidden="true" />
                        : <XCircle className="w-3 h-3 text-red-400 shrink-0" aria-hidden="true" />}
                      {String(label)}
                    </div>
                  ))}
                </div>

                {/* Evidence + dates row */}
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                  {c.testingTool && <span>Tool: <span className="font-medium text-foreground">{c.testingTool}</span></span>}
                  {c.testerName  && <span>Tester: <span className="font-medium text-foreground">{c.testerName}</span></span>}
                  <span>Last test: <span className="font-medium text-foreground">{fmt(c.lastTestDate)}</span></span>
                  {c.nextReviewDate && <span>Review: <span className={`font-medium ${c.reviewOverdue ? "text-red-600" : "text-foreground"}`}>{fmt(c.nextReviewDate)}</span></span>}
                </div>

                {/* Validation codes */}
                {c.validationCodes.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {c.validationCodes.slice(0, 4).map(code => (
                      <Badge key={code} variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200 px-1.5 py-0.5 h-auto">
                        {CODE_LABELS[code] ?? code}
                      </Badge>
                    ))}
                    {c.validationCodes.length > 4 && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-auto">+{c.validationCodes.length - 4} more</Badge>
                    )}
                  </div>
                )}

                {/* Review action */}
                {canManage && c.approvalStatus !== "Approved" && (
                  <div className="pt-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setReviewing(c)}>
                      <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Complete Review
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Dialogs */}
      {(addOpen || editing) && (
        <AddEditDialog
          editing={editing}
          canManage={canManage}
          onClose={() => { setAddOpen(false); setEditing(null); }}
          onSaved={invalidate}
        />
      )}
      {validating && <ValidateDialog control={validating} onClose={() => setValidating(null)} onDone={invalidate} />}
      {running    && <RunTestDialog  control={running}    onClose={() => setRunning(null)}    onDone={invalidate} />}
      {reviewing  && <ReviewDialog   control={reviewing}  onClose={() => setReviewing(null)}  onDone={invalidate} />}
      {deleting   && <DeleteDialog   control={deleting}   onClose={() => setDeleting(null)}   onDone={invalidate} />}
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Chatbot Accessibility — Dynamic Chatbot & AI-Generated Content Accessibility Validation
// ══════════════════════════════════════════════════════════════════════════════

interface ChatbotA11yControl {
  id: string;
  organizationId: string;
  applicationName: string;
  chatbotName: string | null;
  environment: string;
  aiFeatureEnabled: boolean;
  dynamicContentEnabled: boolean;
  screenReaderCompatible: boolean;
  keyboardNavigationSupported: boolean;
  focusManagementImplemented: boolean;
  contentAnnouncementSupported: boolean;
  alternativeTextHandlingEnabled: boolean;
  errorMessageAccessible: boolean;
  contrastValidationCompleted: boolean;
  accessibilityTestingCompleted: boolean;
  testingTool: string | null;
  testEvidenceReference: string | null;
  findingsReference: string | null;
  testerName: string | null;
  lastTestDate: string | null;
  complianceStatus: string;
  riskLevel: string;
  status: string;
  remediationNotes: string | null;
  reviewer: string | null;
  reviewNotes: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  createdAt: string;
  updatedAt: string;
  reviewOverdue: boolean;
  validationCodes: string[];
}

interface ChatbotA11ySummary {
  totalChatbots: number;
  compliantChatbots: number;
  withAiFeatures: number;
  withDynamicContent: number;
  reviewOverdue: number;
  fullyAccessible: number;
}

const WCAG38_CODE_LABELS: Record<string, string> = {
  SCREEN_READER_SUPPORT_REQUIRED:         "Screen reader not supported",
  KEYBOARD_NAVIGATION_REQUIRED:           "Keyboard navigation missing",
  FOCUS_MANAGEMENT_REQUIRED:              "Focus management not implemented",
  DYNAMIC_CONTENT_ANNOUNCEMENT_REQUIRED:  "Dynamic content not announced",
  ALTERNATIVE_TEXT_REQUIRED:              "Alternative text not configured",
  ACCESSIBLE_ERROR_HANDLING_REQUIRED:     "Error handling not accessible",
  CONTRAST_VALIDATION_REQUIRED:           "Contrast not validated",
  ACCESSIBILITY_TESTING_REQUIRED:         "Accessibility testing incomplete",
  TEST_EVIDENCE_REQUIRED:                 "Test evidence missing",
  REVIEW_OVERDUE:                         "Review overdue",
};

const BASE38 = "/api/organization/compliance/wcag/chatbot-accessibility";

function useAuthChatbot() {
  const token =
    (typeof window !== "undefined" &&
      (localStorage.getItem("dotnetbot.jwt") ||
        sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Add / Edit Dialog (WCAG-38) ───────────────────────────────────────────────
function ChatbotAddEditDialog({
  editing, canManage, onClose, onSaved,
}: {
  editing: ChatbotA11yControl | null;
  canManage: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { header } = useAuthChatbot();
  const { toast } = useToast();
  const [form, setForm] = useState({
    applicationName:              editing?.applicationName              ?? "",
    chatbotName:                  editing?.chatbotName                  ?? "",
    environment:                  editing?.environment                  ?? "Production",
    aiFeatureEnabled:             editing?.aiFeatureEnabled             ?? false,
    dynamicContentEnabled:        editing?.dynamicContentEnabled        ?? false,
    screenReaderCompatible:       editing?.screenReaderCompatible       ?? false,
    keyboardNavigationSupported:  editing?.keyboardNavigationSupported  ?? false,
    focusManagementImplemented:   editing?.focusManagementImplemented   ?? false,
    contentAnnouncementSupported: editing?.contentAnnouncementSupported ?? false,
    alternativeTextHandlingEnabled: editing?.alternativeTextHandlingEnabled ?? false,
    errorMessageAccessible:       editing?.errorMessageAccessible       ?? false,
    contrastValidationCompleted:  editing?.contrastValidationCompleted  ?? false,
    accessibilityTestingCompleted:editing?.accessibilityTestingCompleted?? false,
    testingTool:                  editing?.testingTool                  ?? "",
    testEvidenceReference:        editing?.testEvidenceReference        ?? "",
    findingsReference:            editing?.findingsReference            ?? "",
    testerName:                   editing?.testerName                   ?? "",
    riskLevel:                    editing?.riskLevel                    ?? "High",
    status:                       editing?.status                       ?? "Draft",
    remediationNotes:             editing?.remediationNotes             ?? "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: async () => {
      const body = {
        applicationName:              form.applicationName.trim(),
        chatbotName:                  form.chatbotName.trim() || null,
        environment:                  form.environment,
        aiFeatureEnabled:             form.aiFeatureEnabled,
        dynamicContentEnabled:        form.dynamicContentEnabled,
        screenReaderCompatible:       form.screenReaderCompatible,
        keyboardNavigationSupported:  form.keyboardNavigationSupported,
        focusManagementImplemented:   form.focusManagementImplemented,
        contentAnnouncementSupported: form.contentAnnouncementSupported,
        alternativeTextHandlingEnabled: form.alternativeTextHandlingEnabled,
        errorMessageAccessible:       form.errorMessageAccessible,
        contrastValidationCompleted:  form.contrastValidationCompleted,
        accessibilityTestingCompleted:form.accessibilityTestingCompleted,
        testingTool:                  form.testingTool.trim() || null,
        testEvidenceReference:        form.testEvidenceReference.trim() || null,
        findingsReference:            form.findingsReference.trim() || null,
        testerName:                   form.testerName.trim() || null,
        riskLevel:                    form.riskLevel,
        status:                       form.status,
        remediationNotes:             form.remediationNotes.trim() || null,
      };
      const res = editing
        ? await fetch(`${BASE38}/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) })
        : await fetch(BASE38, { method: "POST", headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw Object.assign(new Error(apiErrMsg(res.status)), b); }
      return res.json();
    },
    onSuccess: () => { toast({ title: editing ? "Changes saved successfully." : "Control created successfully." }); onSaved(); onClose(); },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length) {
        setErrors(serverErrs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
    },
  });

  const toggle = (key: keyof typeof form) => setForm(f => ({ ...f, [key]: !f[key as keyof typeof form] }));
  const set = (key: keyof typeof form, val: string) => setForm(f => ({ ...f, [key]: val }));

  const A11Y_CONTROLS: [keyof typeof form, string][] = [
    ["screenReaderCompatible",       "Screen Reader Compatible"],
    ["keyboardNavigationSupported",  "Keyboard Navigation Supported"],
    ["focusManagementImplemented",   "Focus Management Implemented"],
    ["contentAnnouncementSupported", "Dynamic Content Announced (ARIA live regions)"],
    ["alternativeTextHandlingEnabled","Alternative Text Handling Enabled"],
    ["errorMessageAccessible",       "Error Messages Accessible"],
    ["contrastValidationCompleted",  "Contrast Validation Completed"],
    ["accessibilityTestingCompleted","Accessibility Testing Completed"],
  ];

  return (
    <Dialog open onOpenChange={o => { if (!o) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit" : "Add"} Chatbot Accessibility Control</DialogTitle>
          <DialogDescription>Metadata only — no chatbot conversations or AI content stored. No chatbot conversations or AI content is stored.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Application Info */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Application & Chatbot</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Application Name *</Label>
                <Input
                  className={fieldCls("", errors, "applicationName")}
                  value={form.applicationName}
                  onChange={e => { set("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }}
                  aria-invalid={!!errors.applicationName}
                  placeholder="Student Portal"
                />
                {fErr(errors, "applicationName")}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Chatbot Name</Label>
                <Input value={form.chatbotName} onChange={e => set("chatbotName", e.target.value)} placeholder="AcademicBot" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Environment</Label>
                <select value={form.environment} onChange={e => set("environment", e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  {["Development", "Staging", "Production", "All"].map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Risk Level</Label>
                <select value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  {["Low", "Medium", "High", "Critical"].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <select value={form.status} onChange={e => set("status", e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  {["Draft", "Active", "UnderReview", "Approved", "Archived"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {(["aiFeatureEnabled", "dynamicContentEnabled"] as const).map(key => (
                <label key={key} className="flex items-center gap-3 border rounded-md p-2.5 cursor-pointer hover:bg-muted/30">
                  <input type="checkbox" checked={!!form[key]} onChange={() => toggle(key)} className="h-4 w-4 rounded" />
                  <span className="text-sm">{key === "aiFeatureEnabled" ? "AI Feature Enabled" : "Dynamic Content Enabled"}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Accessibility Controls */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Accessibility Controls</p>
            <div className="space-y-2">
              {A11Y_CONTROLS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 border rounded-md p-2.5 cursor-pointer hover:bg-muted/30">
                  <input type="checkbox" checked={!!form[key]} onChange={() => toggle(key)} className="h-4 w-4 rounded" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Testing Details */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Testing Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Testing Tool</Label>
                <Input value={form.testingTool} onChange={e => set("testingTool", e.target.value)} placeholder="axe-core, NVDA, JAWS…" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tester Name</Label>
                <Input value={form.testerName} onChange={e => set("testerName", e.target.value)} placeholder="Jane Smith" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Test Evidence Reference</Label>
                <Input value={form.testEvidenceReference} onChange={e => set("testEvidenceReference", e.target.value)} placeholder="evidence://wcag38-audit-2026.pdf" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Findings Reference</Label>
                <Input value={form.findingsReference} onChange={e => set("findingsReference", e.target.value)} placeholder="findings://chatbot-a11y-report.pdf" />
              </div>
            </div>
          </div>

          {form.remediationNotes !== "" || editing?.remediationNotes ? (
            <div className="space-y-1">
              <Label className="text-xs">Remediation Notes</Label>
              <Textarea value={form.remediationNotes} onChange={e => set("remediationNotes", e.target.value)} rows={3} placeholder="Document remediation actions…" />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "applicationName", form.applicationName || form.chatbotName, "Application Name or Chatbot Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); save.mutate(); }} disabled={save.isPending}>
            {save.isPending ? "Saving…" : editing ? "Save Changes" : "Add Control"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validate Dialog (WCAG-38) ─────────────────────────────────────────────────
function ChatbotValidateDialog({ control, onClose, onDone }: { control: ChatbotA11yControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthChatbot();
  const { toast } = useToast();
  const [result, setResult] = useState<any>(null);
  const validate = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE38}/${control.id}/validate`, { method: "POST", headers: { ...header } });
      if (!res.ok) throw new Error(apiErrMsg(res.status));
      return res.json();
    },
    onSuccess: d => { setResult(d); onDone(); },
    onError: (e: any) => toast({ title: "Validation failed", description: e.message, variant: "destructive" }),
  });

  const CONFIG_CHECKS = [
    ["Screen Reader",     control.screenReaderCompatible],
    ["Keyboard Nav",      control.keyboardNavigationSupported],
    ["Focus Mgmt",        control.focusManagementImplemented],
    ["Content Announce",  control.contentAnnouncementSupported],
    ["Alt Text",          control.alternativeTextHandlingEnabled],
    ["Error Handling",    control.errorMessageAccessible],
    ["Contrast",          control.contrastValidationCompleted],
    ["Testing Done",      control.accessibilityTestingCompleted],
    ["Test Evidence",     !!control.testEvidenceReference],
  ];

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Validate — {control.chatbotName ?? control.applicationName}</DialogTitle>
          <DialogDescription>Runs all 10 accessibility validation checks.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="border rounded-md p-3 bg-muted/20 grid grid-cols-3 gap-1.5 text-xs">
            {CONFIG_CHECKS.map(([label, ok]) => (
              <div key={String(label)} className="flex items-center gap-1.5">
                {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" aria-hidden="true" /> : <XCircle className="w-3.5 h-3.5 text-red-500" aria-hidden="true" />}
                <span className={ok ? "text-foreground" : "text-red-600"}>{String(label)}</span>
              </div>
            ))}
          </div>
          {result && (
            <div className="space-y-3">
              <div className={`border rounded-md p-3 text-sm font-medium flex items-center gap-2 ${result.isCompliant ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
                {result.isCompliant
                  ? <><CheckCircle2 className="w-4 h-4" /> All checks passed — Compliant</>
                  : <><AlertTriangle className="w-4 h-4" /> {result.validationCodes.length} issue{result.validationCodes.length !== 1 ? "s" : ""} found</>}
              </div>
              {result.validationCodes.length > 0 && (
                <div className="space-y-2">
                  {result.validationCodes.map((code: string) => (
                    <div key={code} className="border rounded-md p-2.5 bg-background text-xs space-y-1">
                      <p className="font-medium text-red-700">{code}</p>
                      <p className="text-muted-foreground">{result.remediationSuggestions?.[code] ?? WCAG38_CODE_LABELS[code] ?? code}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => validate.mutate()} disabled={validate.isPending}>
            {validate.isPending ? "Validating…" : result ? "Re-validate" : "Run Validation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Test Simulation Dialog (WCAG-38) ──────────────────────────────────────────
const W38_PRESETS = [
  { label: "Screen reader test",   testType: "Manual",    toolName: "NVDA" },
  { label: "Keyboard-only test",   testType: "Manual",    toolName: "Keyboard" },
  { label: "Automated axe scan",   testType: "Automated", toolName: "axe-core" },
  { label: "Full WCAG 2.1 audit",  testType: "Combined",  toolName: "axe + NVDA" },
];

function ChatbotTestSimDialog({ control, onClose, onDone }: { control: ChatbotA11yControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthChatbot();
  const { toast } = useToast();
  const [form, setForm] = useState({ testType: "Automated", toolName: "", testerName: "", evidenceReference: "" });
  const [result, setResult] = useState<any>(null);

  const run = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE38}/${control.id}/test-simulation`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ testType: form.testType, toolName: form.toolName || null, testerName: form.testerName || null, evidenceReference: form.evidenceReference || null }),
      });
      if (!res.ok) throw new Error(apiErrMsg(res.status));
      return res.json();
    },
    onSuccess: d => { setResult(d); onDone(); },
    onError: (e: any) => toast({ title: "Unable to run simulation", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Test Simulation — {control.chatbotName ?? control.applicationName}</DialogTitle>
          <DialogDescription>
            <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
              <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" /> Simulation only
            </span>
            {" "}— records test metadata; no real scanner executes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-2">
            {W38_PRESETS.map(p => (
              <button key={p.label} type="button"
                className="border rounded-md p-2 text-xs text-left hover:bg-muted/40 transition-colors"
                onClick={() => setForm(f => ({ ...f, testType: p.testType, toolName: p.toolName }))}>
                <p className="font-medium">{p.label}</p>
                <p className="text-muted-foreground">{p.testType} · {p.toolName}</p>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Test Type</Label>
              <select value={form.testType} onChange={e => setForm(f => ({ ...f, testType: e.target.value }))}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                {["Automated", "Manual", "Combined", "Screen Reader", "Keyboard"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tool Name</Label>
              <Input value={form.toolName} onChange={e => setForm(f => ({ ...f, toolName: e.target.value }))} placeholder="axe-core, NVDA…" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tester Name</Label>
              <Input value={form.testerName} onChange={e => setForm(f => ({ ...f, testerName: e.target.value }))} placeholder="Jane Smith" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Evidence Reference</Label>
              <Input value={form.evidenceReference} onChange={e => setForm(f => ({ ...f, evidenceReference: e.target.value }))} placeholder="ci://chatbot-a11y-2026" />
            </div>
          </div>
          {result && (
            <div className="space-y-3 border-t pt-3">
              <div className={`border rounded-md p-3 text-sm font-medium flex items-center gap-2 ${result.passed ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                {result.passed
                  ? <><CheckCircle2 className="w-4 h-4" /> Core accessibility checks passed</>
                  : <><AlertTriangle className="w-4 h-4" /> {result.issues.length} gap{result.issues.length !== 1 ? "s" : ""} detected</>}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ["Screen Reader",    result.screenReaderCompatible],
                  ["Keyboard Nav",     result.keyboardNavigationSupported],
                  ["Focus Mgmt",       result.focusManagementImplemented],
                  ["Content Announce", result.contentAnnouncementSupported],
                ].map(([label, ok]) => (
                  <div key={String(label)} className="border rounded-md p-2 flex items-center gap-1.5">
                    {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-500" aria-hidden="true" /> : <XCircle className="w-4 h-4 text-red-500" aria-hidden="true" />}
                    <span className="font-medium">{String(label)}</span>
                  </div>
                ))}
              </div>
              {result.issues.length > 0 && (
                <div className="space-y-1">
                  {result.issues.map((code: string) => (
                    <div key={code} className="text-xs border rounded px-2 py-1.5 bg-amber-50 text-amber-700">
                      {WCAG38_CODE_LABELS[code] ?? code}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-muted-foreground italic">{result.note}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => run.mutate()} disabled={run.isPending}>
            <Play className="w-3.5 h-3.5 mr-1.5" />
            {run.isPending ? "Running…" : result ? "Run Again" : "Run Simulation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review Dialog (WCAG-38) ───────────────────────────────────────────────────
function ChatbotReviewDialog({ control, onClose, onDone }: { control: ChatbotA11yControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthChatbot();
  const { toast } = useToast();
  const [form, setForm] = useState({ reviewerName: "", reviewNotes: "", nextReviewDate: "" });
  const [reviewerErr, setReviewerErr] = useState("");
  const submit = useMutation({
    mutationFn: async () => {
      if (!form.reviewerName.trim()) { setReviewerErr("Reviewer name is required."); throw new Error("Please fix the highlighted fields."); }
      setReviewerErr("");
      const body: any = { reviewerName: form.reviewerName.trim(), reviewNotes: form.reviewNotes.trim() || null };
      if (form.nextReviewDate) body.nextReviewDate = new Date(form.nextReviewDate).toISOString();
      const res = await fetch(`${BASE38}/${control.id}/review`, { method: "POST", headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(apiErrMsg(res.status));
      return res.json();
    },
    onSuccess: () => { toast({ title: "Review completed successfully." }); onDone(); onClose(); },
    onError: (e: any) => toast({ title: "Unable to save review", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Review — {control.chatbotName ?? control.applicationName}</DialogTitle>
          <DialogDescription>Record the accessibility review outcome for this chatbot control.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="text-xs">Reviewer Name *</Label>
            <Input value={form.reviewerName} onChange={e => { setForm(f => ({ ...f, reviewerName: e.target.value })); if (e.target.value.trim()) setReviewerErr(""); }}
              placeholder="Jane Smith"
              className={reviewerErr ? "border-red-500 focus-visible:ring-red-500" : ""}
              aria-invalid={!!reviewerErr} />
            <FErr msg={reviewerErr} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Next Review Date</Label>
            <Input type="date" value={form.nextReviewDate} onChange={e => setForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Review Notes</Label>
            <Textarea value={form.reviewNotes} onChange={e => setForm(f => ({ ...f, reviewNotes: e.target.value }))} rows={3} placeholder="Observations, follow-up items…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => submit.mutate()} disabled={submit.isPending}>
            {submit.isPending ? "Saving…" : "Complete Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete Dialog (WCAG-38) ───────────────────────────────────────────────────
function ChatbotDeleteDialog({ control, onClose, onDone }: { control: ChatbotA11yControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthChatbot();
  const { toast } = useToast();
  const del = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE38}/${control.id}`, { method: "DELETE", headers: { ...header } });
      if (!res.ok) throw new Error(apiErrMsg(res.status));
    },
    onSuccess: () => { toast({ title: "Control deleted successfully." }); onDone(); onClose(); },
    onError: (e: any) => toast({ title: "Unable to delete", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete Control</DialogTitle>
          <DialogDescription>Delete accessibility control for <strong>{control.chatbotName ?? control.applicationName}</strong>? This cannot be undone.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => del.mutate()} disabled={del.isPending}>
            {del.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Section (WCAG-38) ────────────────────────────────────────────────────
export function ChatbotAccessibilitySection({ canManage }: { canManage: boolean }) {
  const { header } = useAuthChatbot();
  const QKEY38 = [BASE38];

  const [addOpenChatbot, setAddOpenChatbot]       = useState(false);
  const [editingChatbot, setEditingChatbot]       = useState<ChatbotA11yControl | null>(null);
  const [validatingChatbot, setValidatingChatbot] = useState<ChatbotA11yControl | null>(null);
  const [testingChatbot, setTestingChatbot]       = useState<ChatbotA11yControl | null>(null);
  const [reviewingChatbot, setReviewingChatbot]   = useState<ChatbotA11yControl | null>(null);
  const [deletingChatbot, setDeletingChatbot]     = useState<ChatbotA11yControl | null>(null);

  const [f38App, setF38App]         = useState("");
  const [f38Bot, setF38Bot]         = useState("");
  const [f38Env, setF38Env]         = useState("");
  const [f38Comp, setF38Comp]       = useState("");
  const [f38Risk, setF38Risk]       = useState("");

  const invalidateChatbot = () => {
    queryClient.invalidateQueries({ queryKey: [BASE38 + "/summary"] });
    queryClient.invalidateQueries({ queryKey: QKEY38 });
    queryClient.invalidateQueries({ queryKey: [BASE38 + "/issues"] });
  };

  const summaryQ = useQuery<ChatbotA11ySummary>({
    queryKey: [BASE38 + "/summary"],
    queryFn: async () => { const r = await fetch(`${BASE38}/summary`, { headers: { ...header } }); if (!r.ok) throw new Error("Failed"); return r.json(); },
  });
  const issuesQ = useQuery<{ total: number; items: any[] }>({
    queryKey: [BASE38 + "/issues"],
    queryFn: async () => { const r = await fetch(`${BASE38}/issues`, { headers: { ...header } }); if (!r.ok) throw new Error("Failed"); return r.json(); },
  });
  const listQ = useQuery<ChatbotA11yControl[]>({
    queryKey: QKEY38,
    queryFn: async () => {
      const p = new URLSearchParams();
      if (f38App)  p.set("applicationName",  f38App);
      if (f38Bot)  p.set("chatbotName",       f38Bot);
      if (f38Env)  p.set("environment",       f38Env);
      if (f38Comp) p.set("complianceStatus",  f38Comp);
      if (f38Risk) p.set("riskLevel",         f38Risk);
      const r = await fetch(`${BASE38}?${p}`, { headers: { ...header } }); if (!r.ok) throw new Error("Failed"); return r.json();
    },
  });

  const s = summaryQ.data;
  const items38 = listQ.data ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Bot className="w-4 h-4" aria-hidden="true" />
              Chatbot & AI Content Accessibility
            </CardTitle>
            <CardDescription className="mt-1 text-xs">
              Validate accessibility of dynamic chatbot interactions and AI-generated content. Metadata only — no chat messages or AI responses stored.
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => setAddOpenChatbot(true)}>
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary */}
        {s && (
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: "Total Chatbots",    value: s.totalChatbots,      color: "" },
              { label: "Fully Accessible",  value: s.fullyAccessible,    color: s.fullyAccessible === s.totalChatbots && s.totalChatbots > 0 ? "text-emerald-600" : "" },
              { label: "Compliant",         value: s.compliantChatbots,  color: s.compliantChatbots === s.totalChatbots && s.totalChatbots > 0 ? "text-emerald-600" : "" },
              { label: "With AI Features",  value: s.withAiFeatures,     color: "" },
              { label: "Dynamic Content",   value: s.withDynamicContent, color: "" },
              { label: "Review Overdue",    value: s.reviewOverdue,      color: s.reviewOverdue > 0 ? "text-red-600" : "" },
            ].map(m => (
              <div key={m.label} className="border rounded-md p-2.5 text-center">
                <p className={`text-xl font-bold ${m.color || "text-foreground"}`}>{m.value}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{m.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Issues panel */}
        {issuesQ.data && issuesQ.data.total > 0 && (
          <div className="border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 rounded-md p-3 space-y-2">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
              {issuesQ.data.total} chatbot{issuesQ.data.total !== 1 ? "s" : ""} with accessibility gaps
            </p>
            {issuesQ.data.items.slice(0, 5).map((item: any) => (
              <div key={item.control.id} className="bg-background rounded border border-amber-200 dark:border-amber-700 p-2 text-xs">
                <p className="font-medium">{item.control.chatbotName ?? item.control.applicationName}</p>
                <p className="text-muted-foreground mt-0.5">
                  {item.codes.slice(0, 2).map((c: string) => WCAG38_CODE_LABELS[c] ?? c).join(" · ")}
                  {item.codes.length > 2 ? ` +${item.codes.length - 2} more` : ""}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Input placeholder="Filter by app name…" value={f38App} onChange={e => { setF38App(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY38 }); }} className="text-xs h-8" />
          <Input placeholder="Filter by chatbot name…" value={f38Bot} onChange={e => { setF38Bot(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY38 }); }} className="text-xs h-8" />
          <select value={f38Env} onChange={e => { setF38Env(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY38 }); }}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs">
            <option value="">All Environments</option>
            {["Development", "Staging", "Production", "All"].map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <select value={f38Comp} onChange={e => { setF38Comp(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY38 }); }}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs">
            <option value="">All Compliance</option>
            {["Compliant", "NonCompliant", "Pending"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={f38Risk} onChange={e => { setF38Risk(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY38 }); }}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs">
            <option value="">All Risk Levels</option>
            {["Low", "Medium", "High", "Critical"].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={invalidateChatbot}>
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : items38.length === 0 ? (
          <EmptyState
            sub="No chatbot accessibility controls match your filters."
            canManage={canManage}
            onAdd={canManage ? () => setAddOpenChatbot(true) : undefined}
          />
        ) : (
          <div className="space-y-2">
            {items38.map(c => (
              <div key={c.id} className="border rounded-md p-3 space-y-2 hover:bg-muted/20 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Bot className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
                      <span className="text-sm font-medium">{c.chatbotName ?? c.applicationName}</span>
                      {c.chatbotName && <span className="text-xs text-muted-foreground">({c.applicationName})</span>}
                      <EnvBadge env={c.environment} />
                      <RiskBadge level={c.riskLevel} />
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <CompBadge status={c.complianceStatus} />
                      {c.aiFeatureEnabled    && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-purple-100 text-purple-700 border-purple-200">AI</span>}
                      {c.dynamicContentEnabled && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-blue-100 text-blue-700 border-blue-200">Dynamic</span>}
                      {c.reviewOverdue && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-red-100 text-red-700 border-red-200 flex items-center gap-0.5">
                          <Clock className="w-3 h-3" aria-hidden="true" /> Review Overdue
                        </span>
                      )}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setTestingChatbot(c)} title="Run test simulation">
                        <Play className="w-3.5 h-3.5 mr-1" /> Test
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setValidatingChatbot(c)} title="Validate">
                        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Validate
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingChatbot(c)}>Edit</Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setDeletingChatbot(c)}>
                        <Trash2 className="w-3.5 h-3.5 text-destructive" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* 8-control accessibility grid */}
                <div className="grid grid-cols-4 gap-1 text-[10px]">
                  {[
                    ["Screen Reader", c.screenReaderCompatible],
                    ["Keyboard",      c.keyboardNavigationSupported],
                    ["Focus Mgmt",    c.focusManagementImplemented],
                    ["Announce",      c.contentAnnouncementSupported],
                    ["Alt Text",      c.alternativeTextHandlingEnabled],
                    ["Errors",        c.errorMessageAccessible],
                    ["Contrast",      c.contrastValidationCompleted],
                    ["Tested",        c.accessibilityTestingCompleted],
                  ].map(([label, ok]) => (
                    <div key={String(label)} className="flex items-center gap-1 text-muted-foreground">
                      {ok
                        ? <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" aria-hidden="true" />
                        : <XCircle className="w-3 h-3 text-red-400 shrink-0" aria-hidden="true" />}
                      {String(label)}
                    </div>
                  ))}
                </div>

                {/* Evidence row */}
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                  {c.testingTool   && <span>Tool: <span className="font-medium text-foreground">{c.testingTool}</span></span>}
                  {c.testerName   && <span>Tester: <span className="font-medium text-foreground">{c.testerName}</span></span>}
                  <span>Last test: <span className="font-medium text-foreground">{fmt(c.lastTestDate)}</span></span>
                  {c.nextReviewDate && <span>Review: <span className={`font-medium ${c.reviewOverdue ? "text-red-600" : "text-foreground"}`}>{fmt(c.nextReviewDate)}</span></span>}
                </div>

                {/* Validation codes */}
                {c.validationCodes.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {c.validationCodes.slice(0, 4).map(code => (
                      <Badge key={code} variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200 px-1.5 py-0.5 h-auto">
                        {WCAG38_CODE_LABELS[code] ?? code}
                      </Badge>
                    ))}
                    {c.validationCodes.length > 4 && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-auto">+{c.validationCodes.length - 4} more</Badge>
                    )}
                  </div>
                )}

                {/* Review action */}
                {canManage && (
                  <div className="pt-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setReviewingChatbot(c)}>
                      <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Complete Review
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {(addOpenChatbot || editingChatbot) && (
        <ChatbotAddEditDialog editing={editingChatbot} canManage={canManage} onClose={() => { setAddOpenChatbot(false); setEditingChatbot(null); }} onSaved={invalidateChatbot} />
      )}
      {validatingChatbot && <ChatbotValidateDialog control={validatingChatbot} onClose={() => setValidatingChatbot(null)} onDone={invalidateChatbot} />}
      {testingChatbot   && <ChatbotTestSimDialog   control={testingChatbot}    onClose={() => setTestingChatbot(null)}    onDone={invalidateChatbot} />}
      {reviewingChatbot && <ChatbotReviewDialog    control={reviewingChatbot}  onClose={() => setReviewingChatbot(null)}  onDone={invalidateChatbot} />}
      {deletingChatbot  && <ChatbotDeleteDialog    control={deletingChatbot}   onClose={() => setDeletingChatbot(null)}   onDone={invalidateChatbot} />}
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Accessible Forms — Accessible Forms, Error Messages, Labels, Focus & Keyboard Flows
// ══════════════════════════════════════════════════════════════════════════════

interface FormsA11yControl {
  id: string;
  applicationName: string;
  formModuleName: string | null;
  environment: string;
  labelsProvided: boolean;
  labelsAssociatedCorrectly: boolean;
  errorMessagesAccessible: boolean;
  errorMessageDescriptionsProvided: boolean;
  requiredFieldsClearlyIdentified: boolean;
  inlineValidationSupported: boolean;
  focusManagementImplemented: boolean;
  focusMovedToErrors: boolean;
  keyboardNavigationSupported: boolean;
  tabOrderValidated: boolean;
  keyboardOnlyTestingCompleted: boolean;
  screenReaderFormTestingCompleted: boolean;
  testingTool: string | null;
  testEvidenceReference: string | null;
  findingsReference: string | null;
  testerName: string | null;
  lastTestDate: string | null;
  complianceStatus: string;
  riskLevel: string;
  status: string;
  remediationNotes: string | null;
  reviewer: string | null;
  reviewNotes: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  createdAt: string;
  updatedAt: string;
  reviewOverdue: boolean;
  validationCodes: string[];
}

interface FormsA11ySummary {
  totalForms: number;
  compliantForms: number;
  fullyAccessible: number;
  withEvidence: number;
  reviewOverdue: number;
  testedByKeyboard: number;
}

const WCAG39_CODE_LABELS: Record<string, string> = {
  FORM_LABELS_MISSING:                 "Form labels missing",
  LABEL_ASSOCIATION_REQUIRED:          "Label association missing",
  ACCESSIBLE_ERROR_MESSAGES_REQUIRED:  "Error messages not accessible",
  ERROR_DESCRIPTION_REQUIRED:          "Error descriptions missing",
  REQUIRED_FIELD_INDICATION_MISSING:   "Required field indication missing",
  INLINE_VALIDATION_REQUIRED:          "Inline validation missing",
  FOCUS_MANAGEMENT_REQUIRED:           "Focus management not implemented",
  ERROR_FOCUS_REQUIRED:                "Focus not moved to errors",
  KEYBOARD_NAVIGATION_REQUIRED:        "Keyboard navigation missing",
  TAB_ORDER_VALIDATION_REQUIRED:       "Tab order not validated",
  KEYBOARD_TESTING_REQUIRED:           "Keyboard-only testing incomplete",
  SCREEN_READER_TESTING_REQUIRED:      "Screen reader testing incomplete",
  TEST_EVIDENCE_REQUIRED:              "Test evidence missing",
  REVIEW_OVERDUE:                      "Review overdue",
};

const BASE39 = "/api/organization/compliance/wcag/accessible-forms";

function useAuthForms() {
  const token =
    (typeof window !== "undefined" &&
      (localStorage.getItem("dotnetbot.jwt") ||
        sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

const FORMS_CONTROLS_LABELS: [keyof FormsA11yControl, string][] = [
  ["labelsProvided",                  "Labels Provided"],
  ["labelsAssociatedCorrectly",       "Labels Associated Correctly"],
  ["errorMessagesAccessible",         "Error Messages Accessible"],
  ["errorMessageDescriptionsProvided","Error Descriptions Provided"],
  ["requiredFieldsClearlyIdentified", "Required Fields Identified"],
  ["inlineValidationSupported",       "Inline Validation"],
  ["focusManagementImplemented",      "Focus Management"],
  ["focusMovedToErrors",              "Focus Moved to Errors"],
  ["keyboardNavigationSupported",     "Keyboard Navigation"],
  ["tabOrderValidated",               "Tab Order Validated"],
  ["keyboardOnlyTestingCompleted",    "Keyboard-Only Testing"],
  ["screenReaderFormTestingCompleted","Screen Reader Testing"],
];

function FormsAddEditDialog({ editing, canManage, onClose, onSaved }: {
  editing: FormsA11yControl | null; canManage: boolean; onClose: () => void; onSaved: () => void;
}) {
  const { header } = useAuthForms();
  const { toast } = useToast();
  const [form, setForm] = useState({
    applicationName:                  editing?.applicationName                  ?? "",
    formModuleName:                   editing?.formModuleName                   ?? "",
    environment:                      editing?.environment                      ?? "Production",
    labelsProvided:                   editing?.labelsProvided                   ?? false,
    labelsAssociatedCorrectly:        editing?.labelsAssociatedCorrectly        ?? false,
    errorMessagesAccessible:          editing?.errorMessagesAccessible          ?? false,
    errorMessageDescriptionsProvided: editing?.errorMessageDescriptionsProvided ?? false,
    requiredFieldsClearlyIdentified:  editing?.requiredFieldsClearlyIdentified  ?? false,
    inlineValidationSupported:        editing?.inlineValidationSupported        ?? false,
    focusManagementImplemented:       editing?.focusManagementImplemented       ?? false,
    focusMovedToErrors:               editing?.focusMovedToErrors               ?? false,
    keyboardNavigationSupported:      editing?.keyboardNavigationSupported      ?? false,
    tabOrderValidated:                editing?.tabOrderValidated                ?? false,
    keyboardOnlyTestingCompleted:     editing?.keyboardOnlyTestingCompleted     ?? false,
    screenReaderFormTestingCompleted: editing?.screenReaderFormTestingCompleted ?? false,
    testingTool:                      editing?.testingTool                      ?? "",
    testEvidenceReference:            editing?.testEvidenceReference            ?? "",
    findingsReference:                editing?.findingsReference                ?? "",
    testerName:                       editing?.testerName                       ?? "",
    riskLevel:                        editing?.riskLevel                        ?? "High",
    status:                           editing?.status                           ?? "Draft",
    remediationNotes:                 editing?.remediationNotes                 ?? "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: async () => {
      const body = {
        applicationName:                  form.applicationName.trim(),
        formModuleName:                   form.formModuleName.trim() || null,
        environment:                      form.environment,
        labelsProvided:                   form.labelsProvided,
        labelsAssociatedCorrectly:        form.labelsAssociatedCorrectly,
        errorMessagesAccessible:          form.errorMessagesAccessible,
        errorMessageDescriptionsProvided: form.errorMessageDescriptionsProvided,
        requiredFieldsClearlyIdentified:  form.requiredFieldsClearlyIdentified,
        inlineValidationSupported:        form.inlineValidationSupported,
        focusManagementImplemented:       form.focusManagementImplemented,
        focusMovedToErrors:               form.focusMovedToErrors,
        keyboardNavigationSupported:      form.keyboardNavigationSupported,
        tabOrderValidated:                form.tabOrderValidated,
        keyboardOnlyTestingCompleted:     form.keyboardOnlyTestingCompleted,
        screenReaderFormTestingCompleted: form.screenReaderFormTestingCompleted,
        testingTool:                      form.testingTool.trim() || null,
        testEvidenceReference:            form.testEvidenceReference.trim() || null,
        findingsReference:                form.findingsReference.trim() || null,
        testerName:                       form.testerName.trim() || null,
        riskLevel:                        form.riskLevel,
        status:                           form.status,
        remediationNotes:                 form.remediationNotes.trim() || null,
      };
      const res = editing
        ? await fetch(`${BASE39}/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) })
        : await fetch(BASE39, { method: "POST", headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw Object.assign(new Error(apiErrMsg(res.status)), b); }
      return res.json();
    },
    onSuccess: () => { toast({ title: editing ? "Changes saved successfully." : "Control created successfully." }); onSaved(); onClose(); },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length) {
        setErrors(serverErrs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
    },
  });

  const toggle = (key: keyof typeof form) => setForm(f => ({ ...f, [key]: !f[key as keyof typeof form] }));
  const set = (key: keyof typeof form, val: string) => setForm(f => ({ ...f, [key]: val }));

  const LABEL_SECTION: [keyof typeof form, string][] = [
    ["labelsProvided",            "Labels Provided"],
    ["labelsAssociatedCorrectly", "Labels Associated Correctly (for/id or aria-labelledby)"],
  ];
  const ERROR_SECTION: [keyof typeof form, string][] = [
    ["errorMessagesAccessible",          "Error Messages Accessible (aria-live or aria-describedby)"],
    ["errorMessageDescriptionsProvided", "Descriptive Error Descriptions Provided"],
    ["requiredFieldsClearlyIdentified",  "Required Fields Clearly Identified (asterisk + aria-required)"],
    ["inlineValidationSupported",        "Inline Per-Field Validation Supported"],
  ];
  const FOCUS_SECTION: [keyof typeof form, string][] = [
    ["focusManagementImplemented", "Focus Management Implemented"],
    ["focusMovedToErrors",         "Focus Moved to Errors on Submit"],
  ];
  const KEYBOARD_SECTION: [keyof typeof form, string][] = [
    ["keyboardNavigationSupported",      "Keyboard Navigation Supported"],
    ["tabOrderValidated",                "Tab Order Validated"],
    ["keyboardOnlyTestingCompleted",     "Keyboard-Only Testing Completed"],
    ["screenReaderFormTestingCompleted", "Screen Reader Form Testing Completed"],
  ];

  return (
    <Dialog open onOpenChange={o => { if (!o) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit" : "Add"} Accessible Forms Control</DialogTitle>
          <DialogDescription>Metadata only — no form submissions, user input, or student data stored. No form submissions, user input, or student data is stored.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* App info */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Application & Form</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Application Name *</Label>
                <Input
                  className={fieldCls("", errors, "applicationName")}
                  value={form.applicationName}
                  onChange={e => { set("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }}
                  aria-invalid={!!errors.applicationName}
                  placeholder="Student Registration Portal"
                />
                {fErr(errors, "applicationName")}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Form / Module Name</Label>
                <Input value={form.formModuleName} onChange={e => set("formModuleName", e.target.value)} placeholder="EnrollmentForm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Environment</Label>
                <select value={form.environment} onChange={e => set("environment", e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  {["Development", "Staging", "Production", "All"].map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Risk Level</Label>
                <select value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  {["Low", "Medium", "High", "Critical"].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <select value={form.status} onChange={e => set("status", e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  {["Draft", "Active", "UnderReview", "Approved", "Archived"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Labels */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Labels</p>
            <div className="space-y-2">
              {LABEL_SECTION.map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 border rounded-md p-2.5 cursor-pointer hover:bg-muted/30">
                  <input type="checkbox" checked={!!form[key]} onChange={() => toggle(key)} className="h-4 w-4 rounded" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Error handling */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Error Handling & Validation</p>
            <div className="space-y-2">
              {ERROR_SECTION.map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 border rounded-md p-2.5 cursor-pointer hover:bg-muted/30">
                  <input type="checkbox" checked={!!form[key]} onChange={() => toggle(key)} className="h-4 w-4 rounded" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Focus */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Focus Management</p>
            <div className="space-y-2">
              {FOCUS_SECTION.map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 border rounded-md p-2.5 cursor-pointer hover:bg-muted/30">
                  <input type="checkbox" checked={!!form[key]} onChange={() => toggle(key)} className="h-4 w-4 rounded" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Keyboard */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Keyboard & Testing</p>
            <div className="space-y-2">
              {KEYBOARD_SECTION.map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 border rounded-md p-2.5 cursor-pointer hover:bg-muted/30">
                  <input type="checkbox" checked={!!form[key]} onChange={() => toggle(key)} className="h-4 w-4 rounded" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Testing metadata */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Testing Metadata</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Testing Tool</Label>
                <Input value={form.testingTool} onChange={e => set("testingTool", e.target.value)} placeholder="axe-core, NVDA, keyboard…" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tester Name</Label>
                <Input value={form.testerName} onChange={e => set("testerName", e.target.value)} placeholder="Jane Smith" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Test Evidence Reference</Label>
                <Input value={form.testEvidenceReference} onChange={e => set("testEvidenceReference", e.target.value)} placeholder="evidence://wcag39-forms-audit-2026.pdf" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Findings Reference</Label>
                <Input value={form.findingsReference} onChange={e => set("findingsReference", e.target.value)} placeholder="findings://form-a11y-report.pdf" />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "applicationName", form.applicationName, "Application Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); save.mutate(); }} disabled={save.isPending}>
            {save.isPending ? "Saving…" : editing ? "Save Changes" : "Add Control"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormsValidateDialog({ control, onClose, onDone }: { control: FormsA11yControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthForms();
  const { toast } = useToast();
  const [result, setResult] = useState<any>(null);

  const validate = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE39}/${control.id}/validate`, { method: "POST", headers: { ...header } });
      if (!res.ok) throw new Error(apiErrMsg(res.status));
      return res.json();
    },
    onSuccess: d => { setResult(d); onDone(); },
    onError: (e: any) => toast({ title: "Validation failed", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Validate — {control.formModuleName ?? control.applicationName}</DialogTitle>
          <DialogDescription>Runs all 14 accessible forms checks.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="border rounded-md p-3 bg-muted/20 grid grid-cols-2 gap-1.5 text-xs">
            {FORMS_CONTROLS_LABELS.map(([key, label]) => (
              <div key={key} className="flex items-center gap-1.5">
                {control[key]
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" aria-hidden="true" />
                  : <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" aria-hidden="true" />}
                <span className={control[key] ? "" : "text-red-600"}>{label}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5">
              {control.testEvidenceReference
                ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" aria-hidden="true" />
                : <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" aria-hidden="true" />}
              <span className={control.testEvidenceReference ? "" : "text-red-600"}>Evidence Reference</span>
            </div>
          </div>
          {result && (
            <div className="space-y-3">
              <div className={`border rounded-md p-3 text-sm font-medium flex items-center gap-2 ${result.isCompliant ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
                {result.isCompliant
                  ? <><CheckCircle2 className="w-4 h-4" /> All checks passed — Compliant</>
                  : <><AlertTriangle className="w-4 h-4" /> {result.validationCodes.length} issue{result.validationCodes.length !== 1 ? "s" : ""} found</>}
              </div>
              {result.validationCodes.length > 0 && (
                <div className="space-y-2">
                  {result.validationCodes.map((code: string) => (
                    <div key={code} className="border rounded-md p-2.5 text-xs space-y-1">
                      <p className="font-medium text-red-700">{code}</p>
                      <p className="text-muted-foreground">{result.remediationSuggestions?.[code] ?? WCAG39_CODE_LABELS[code] ?? code}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => validate.mutate()} disabled={validate.isPending}>
            {validate.isPending ? "Validating…" : result ? "Re-validate" : "Run Validation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const W39_PRESETS = [
  { label: "Keyboard-only test",   testType: "Keyboard",   toolName: "Manual keyboard" },
  { label: "Screen reader test",   testType: "Manual",     toolName: "NVDA + Chrome" },
  { label: "Automated axe scan",   testType: "Automated",  toolName: "axe-core" },
  { label: "Full form WCAG audit", testType: "Combined",   toolName: "axe + NVDA" },
];

function FormsTestSimDialog({ control, onClose, onDone }: { control: FormsA11yControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthForms();
  const { toast } = useToast();
  const [form, setForm] = useState({ testType: "Keyboard", toolName: "", testerName: "", evidenceReference: "" });
  const [result, setResult] = useState<any>(null);

  const run = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE39}/${control.id}/test-simulation`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ testType: form.testType, toolName: form.toolName || null, testerName: form.testerName || null, evidenceReference: form.evidenceReference || null }),
      });
      if (!res.ok) throw new Error(apiErrMsg(res.status));
      return res.json();
    },
    onSuccess: d => { setResult(d); onDone(); },
    onError: (e: any) => toast({ title: "Unable to run simulation", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Test Simulation — {control.formModuleName ?? control.applicationName}</DialogTitle>
          <DialogDescription>
            <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
              <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" /> Simulation only
            </span>
            {" "}— records test metadata; no real accessibility scanner executes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-2">
            {W39_PRESETS.map(p => (
              <button key={p.label} type="button"
                className="border rounded-md p-2 text-xs text-left hover:bg-muted/40 transition-colors"
                onClick={() => setForm(f => ({ ...f, testType: p.testType, toolName: p.toolName }))}>
                <p className="font-medium">{p.label}</p>
                <p className="text-muted-foreground">{p.testType} · {p.toolName}</p>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Test Type</Label>
              <select value={form.testType} onChange={e => setForm(f => ({ ...f, testType: e.target.value }))}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                {["Automated", "Manual", "Keyboard", "Combined", "Screen Reader"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tool Name</Label>
              <Input value={form.toolName} onChange={e => setForm(f => ({ ...f, toolName: e.target.value }))} placeholder="axe-core, NVDA…" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tester Name</Label>
              <Input value={form.testerName} onChange={e => setForm(f => ({ ...f, testerName: e.target.value }))} placeholder="Jane Smith" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Evidence Reference</Label>
              <Input value={form.evidenceReference} onChange={e => setForm(f => ({ ...f, evidenceReference: e.target.value }))} placeholder="ci://form-a11y-2026" />
            </div>
          </div>
          {result && (
            <div className="space-y-3 border-t pt-3">
              <div className={`border rounded-md p-3 text-sm font-medium flex items-center gap-2 ${result.passed ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                {result.passed
                  ? <><CheckCircle2 className="w-4 h-4" /> Core form accessibility checks passed</>
                  : <><AlertTriangle className="w-4 h-4" /> {result.issues.length} gap{result.issues.length !== 1 ? "s" : ""} detected</>}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ["Labels",   result.labelsOk],
                  ["Focus",    result.focusOk],
                  ["Keyboard", result.keyboardOk],
                  ["Testing",  result.testingOk],
                ].map(([label, ok]) => (
                  <div key={String(label)} className="border rounded-md p-2 flex items-center gap-1.5">
                    {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-500" aria-hidden="true" /> : <XCircle className="w-4 h-4 text-red-500" aria-hidden="true" />}
                    <span className="font-medium">{String(label)}</span>
                  </div>
                ))}
              </div>
              {result.issues.length > 0 && (
                <div className="space-y-1">
                  {result.issues.map((issue: string, i: number) => (
                    <div key={i} className="text-xs border rounded px-2 py-1.5 bg-amber-50 text-amber-700">{issue}</div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-muted-foreground italic">{result.note}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => run.mutate()} disabled={run.isPending}>
            <Play className="w-3.5 h-3.5 mr-1.5" />
            {run.isPending ? "Running…" : result ? "Run Again" : "Run Simulation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormsReviewDialog({ control, onClose, onDone }: { control: FormsA11yControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthForms();
  const { toast } = useToast();
  const [form, setForm] = useState({ reviewerName: "", reviewNotes: "", nextReviewDate: "" });
  const [reviewerErr, setReviewerErr] = useState("");
  const submit = useMutation({
    mutationFn: async () => {
      if (!form.reviewerName.trim()) { setReviewerErr("Reviewer name is required."); throw new Error("Please fix the highlighted fields."); }
      setReviewerErr("");
      const body: any = { reviewerName: form.reviewerName.trim(), reviewNotes: form.reviewNotes.trim() || null };
      if (form.nextReviewDate) body.nextReviewDate = new Date(form.nextReviewDate).toISOString();
      const res = await fetch(`${BASE39}/${control.id}/review`, { method: "POST", headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(apiErrMsg(res.status));
      return res.json();
    },
    onSuccess: () => { toast({ title: "Review completed successfully." }); onDone(); onClose(); },
    onError: (e: any) => toast({ title: "Unable to save review", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Review — {control.formModuleName ?? control.applicationName}</DialogTitle>
          <DialogDescription>Record the accessible forms review outcome.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1"><Label className="text-xs">Reviewer Name *</Label>
            <Input value={form.reviewerName} onChange={e => { setForm(f => ({ ...f, reviewerName: e.target.value })); if (e.target.value.trim()) setReviewerErr(""); }}
              placeholder="Jane Smith"
              className={reviewerErr ? "border-red-500 focus-visible:ring-red-500" : ""}
              aria-invalid={!!reviewerErr} />
            <FErr msg={reviewerErr} /></div>
          <div className="space-y-1"><Label className="text-xs">Next Review Date</Label>
            <Input type="date" value={form.nextReviewDate} onChange={e => setForm(f => ({ ...f, nextReviewDate: e.target.value }))} /></div>
          <div className="space-y-1"><Label className="text-xs">Review Notes</Label>
            <Textarea value={form.reviewNotes} onChange={e => setForm(f => ({ ...f, reviewNotes: e.target.value }))} rows={3} placeholder="Observations, follow-up items…" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => submit.mutate()} disabled={submit.isPending}>
            {submit.isPending ? "Saving…" : "Complete Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormsDeleteDialog({ control, onClose, onDone }: { control: FormsA11yControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthForms();
  const { toast } = useToast();
  const del = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE39}/${control.id}`, { method: "DELETE", headers: { ...header } });
      if (!res.ok) throw new Error(apiErrMsg(res.status));
    },
    onSuccess: () => { toast({ title: "Control deleted successfully." }); onDone(); onClose(); },
    onError: (e: any) => toast({ title: "Unable to delete", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete Control</DialogTitle>
          <DialogDescription>Delete accessible forms control for <strong>{control.formModuleName ?? control.applicationName}</strong>? This cannot be undone.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => del.mutate()} disabled={del.isPending}>
            {del.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AccessibleFormsSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuthForms();
  const QKEY39 = [BASE39];

  const [addOpenForms, setAddOpenForms]       = useState(false);
  const [editingForms, setEditingForms]       = useState<FormsA11yControl | null>(null);
  const [validatingForms, setValidatingForms] = useState<FormsA11yControl | null>(null);
  const [testingForms, setTestingForms]       = useState<FormsA11yControl | null>(null);
  const [reviewingForms, setReviewingForms]   = useState<FormsA11yControl | null>(null);
  const [deletingForms, setDeletingForms]     = useState<FormsA11yControl | null>(null);

  const [f39App,  setF39App]  = useState("");
  const [f39Form, setF39Form] = useState("");
  const [f39Env,  setF39Env]  = useState("");
  const [f39Comp, setF39Comp] = useState("");
  const [f39Risk, setF39Risk] = useState("");

  const invalidateForms = () => {
    queryClient.invalidateQueries({ queryKey: [BASE39 + "/summary"] });
    queryClient.invalidateQueries({ queryKey: QKEY39 });
    queryClient.invalidateQueries({ queryKey: [BASE39 + "/issues"] });
  };

  const summaryQ = useQuery<FormsA11ySummary>({
    queryKey: [BASE39 + "/summary"],
    queryFn: async () => { const r = await fetch(`${BASE39}/summary`, { headers: { ...header } }); if (!r.ok) throw new Error("Failed"); return r.json(); },
  });
  const issuesQ = useQuery<{ total: number; items: any[] }>({
    queryKey: [BASE39 + "/issues"],
    queryFn: async () => { const r = await fetch(`${BASE39}/issues`, { headers: { ...header } }); if (!r.ok) throw new Error("Failed"); return r.json(); },
  });
  const listQ = useQuery<FormsA11yControl[]>({
    queryKey: QKEY39,
    queryFn: async () => {
      const p = new URLSearchParams();
      if (f39App)  p.set("applicationName",  f39App);
      if (f39Form) p.set("formModuleName",    f39Form);
      if (f39Env)  p.set("environment",       f39Env);
      if (f39Comp) p.set("complianceStatus",  f39Comp);
      if (f39Risk) p.set("riskLevel",         f39Risk);
      const r = await fetch(`${BASE39}?${p}`, { headers: { ...header } }); if (!r.ok) throw new Error("Failed"); return r.json();
    },
  });

  const s = summaryQ.data;
  const items39 = listQ.data ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4" aria-hidden="true" />
              Accessible Forms & Keyboard Flows
            </CardTitle>
            <CardDescription className="mt-1 text-xs">
              Validate accessible labels, error messages, focus management, and keyboard navigation for forms. Metadata only — no form submissions stored.
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => setAddOpenForms(true)}>
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary */}
        {s && (
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: "Total Forms",       value: s.totalForms,       color: "" },
              { label: "Fully Accessible",  value: s.fullyAccessible,  color: s.fullyAccessible === s.totalForms && s.totalForms > 0 ? "text-emerald-600" : "" },
              { label: "Compliant",         value: s.compliantForms,   color: s.compliantForms  === s.totalForms && s.totalForms > 0 ? "text-emerald-600" : "" },
              { label: "With Evidence",     value: s.withEvidence,     color: "" },
              { label: "Keyboard Tested",   value: s.testedByKeyboard, color: "" },
              { label: "Review Overdue",    value: s.reviewOverdue,    color: s.reviewOverdue > 0 ? "text-red-600" : "" },
            ].map(m => (
              <div key={m.label} className="border rounded-md p-2.5 text-center">
                <p className={`text-xl font-bold ${m.color || "text-foreground"}`}>{m.value}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{m.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Issues */}
        {issuesQ.data && issuesQ.data.total > 0 && (
          <div className="border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 rounded-md p-3 space-y-2">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
              {issuesQ.data.total} form{issuesQ.data.total !== 1 ? "s" : ""} with accessibility gaps
            </p>
            {issuesQ.data.items.slice(0, 5).map((item: any) => (
              <div key={item.control.id} className="bg-background rounded border border-amber-200 dark:border-amber-700 p-2 text-xs">
                <p className="font-medium">{item.control.formModuleName ?? item.control.applicationName}</p>
                <p className="text-muted-foreground mt-0.5">
                  {item.codes.slice(0, 2).map((c: string) => WCAG39_CODE_LABELS[c] ?? c).join(" · ")}
                  {item.codes.length > 2 ? ` +${item.codes.length - 2} more` : ""}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Input placeholder="Filter by app…" value={f39App} onChange={e => { setF39App(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY39 }); }} className="text-xs h-8" />
          <Input placeholder="Filter by form/module…" value={f39Form} onChange={e => { setF39Form(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY39 }); }} className="text-xs h-8" />
          <select value={f39Env} onChange={e => { setF39Env(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY39 }); }}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs">
            <option value="">All Environments</option>
            {["Development", "Staging", "Production", "All"].map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <select value={f39Comp} onChange={e => { setF39Comp(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY39 }); }}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs">
            <option value="">All Compliance</option>
            {["Compliant", "NonCompliant", "Pending"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={f39Risk} onChange={e => { setF39Risk(e.target.value); queryClient.invalidateQueries({ queryKey: QKEY39 }); }}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs">
            <option value="">All Risk Levels</option>
            {["Low", "Medium", "High", "Critical"].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={invalidateForms}>
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : items39.length === 0 ? (
          <EmptyState
            sub="No accessible forms controls match your filters."
            canManage={canManage}
            onAdd={canManage ? () => setAddOpenForms(true) : undefined}
          />
        ) : (
          <div className="space-y-2">
            {items39.map(c => (
              <div key={c.id} className="border rounded-md p-3 space-y-2 hover:bg-muted/20 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-medium">{c.formModuleName ?? c.applicationName}</span>
                      {c.formModuleName && <span className="text-xs text-muted-foreground">({c.applicationName})</span>}
                      <EnvBadge env={c.environment} />
                      <RiskBadge level={c.riskLevel} />
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <CompBadge status={c.complianceStatus} />
                      {c.reviewOverdue && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-red-100 text-red-700 border-red-200 flex items-center gap-0.5">
                          <Clock className="w-3 h-3" aria-hidden="true" /> Review Overdue
                        </span>
                      )}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setTestingForms(c)}>
                        <Play className="w-3.5 h-3.5 mr-1" /> Test
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setValidatingForms(c)}>
                        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Validate
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingForms(c)}>Edit</Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setDeletingForms(c)}>
                        <Trash2 className="w-3.5 h-3.5 text-destructive" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* 12-control grid (3 rows of 4) */}
                <div className="grid grid-cols-4 gap-1 text-[10px]">
                  {FORMS_CONTROLS_LABELS.map(([key, label]) => (
                    <div key={key} className="flex items-center gap-1 text-muted-foreground">
                      {c[key]
                        ? <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" aria-hidden="true" />
                        : <XCircle className="w-3 h-3 text-red-400 shrink-0" aria-hidden="true" />}
                      {label}
                    </div>
                  ))}
                </div>

                {/* Evidence row */}
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                  {c.testingTool && <span>Tool: <span className="font-medium text-foreground">{c.testingTool}</span></span>}
                  {c.testerName  && <span>Tester: <span className="font-medium text-foreground">{c.testerName}</span></span>}
                  <span>Last test: <span className="font-medium text-foreground">{fmt(c.lastTestDate)}</span></span>
                  {c.nextReviewDate && <span>Review: <span className={`font-medium ${c.reviewOverdue ? "text-red-600" : "text-foreground"}`}>{fmt(c.nextReviewDate)}</span></span>}
                </div>

                {/* Validation codes */}
                {c.validationCodes.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {c.validationCodes.slice(0, 4).map(code => (
                      <Badge key={code} variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200 px-1.5 py-0.5 h-auto">
                        {WCAG39_CODE_LABELS[code] ?? code}
                      </Badge>
                    ))}
                    {c.validationCodes.length > 4 && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-auto">+{c.validationCodes.length - 4} more</Badge>
                    )}
                  </div>
                )}

                {canManage && (
                  <div className="pt-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setReviewingForms(c)}>
                      <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Complete Review
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {(addOpenForms || editingForms) && (
        <FormsAddEditDialog editing={editingForms} canManage={canManage} onClose={() => { setAddOpenForms(false); setEditingForms(null); }} onSaved={invalidateForms} />
      )}
      {validatingForms && <FormsValidateDialog control={validatingForms} onClose={() => setValidatingForms(null)} onDone={invalidateForms} />}
      {testingForms   && <FormsTestSimDialog   control={testingForms}    onClose={() => setTestingForms(null)}    onDone={invalidateForms} />}
      {reviewingForms && <FormsReviewDialog    control={reviewingForms}  onClose={() => setReviewingForms(null)}  onDone={invalidateForms} />}
      {deletingForms  && <FormsDeleteDialog    control={deletingForms}   onClose={() => setDeletingForms(null)}   onDone={invalidateForms} />}
    </Card>
  );
}

// ── WCAG 2.2 AA Compliance Assessment ────────────────────────────────────────

interface WCAControl {
  id: string; applicationName: string; moduleName?: string; environment: string;
  assessmentType?: string; assessmentDate?: string; assessorName?: string; assessmentTool?: string;
  manualTestingPerformed: boolean; automatedTestingPerformed: boolean;
  perceivableValidated: boolean; operableValidated: boolean;
  understandableValidated: boolean; robustValidated: boolean;
  keyboardAccessibilityValidated: boolean; focusAppearanceValidated: boolean;
  focusOrderValidated: boolean; contrastValidated: boolean;
  textAlternativesValidated: boolean; formsAccessibilityValidated: boolean;
  responsiveAccessibilityValidated: boolean; cognitiveAccessibilityValidated: boolean;
  findingsExist: boolean; findingsSummary?: string; findingsReference?: string;
  evidenceReference?: string;
  complianceStatus: string; riskLevel: string; status: string;
  remediationNotes?: string; reviewer?: string; reviewNotes?: string;
  lastReviewDate?: string; nextReviewDate?: string; reviewOverdue: boolean;
  validationCodes: string[];
}
interface WCASummary {
  totalApplications: number; fullyCompliant: number; assessmentsCompleted: number;
  findingsOpen: number; evidenceMissing: number; reviewsOverdue: number;
}

function useAuthWCA() {
  const token = localStorage.getItem("dotnetbot.jwt") ?? sessionStorage.getItem("dotnetbot.jwt") ?? "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

const WCA_PRINCIPLES: [keyof WCAControl, string][] = [
  ["perceivableValidated",    "Perceivable"],
  ["operableValidated",       "Operable"],
  ["understandableValidated", "Understandable"],
  ["robustValidated",         "Robust"],
];

const WCA_CRITERIA: [keyof WCAControl, string][] = [
  ["keyboardAccessibilityValidated",   "Keyboard"],
  ["focusAppearanceValidated",         "Focus Appearance"],
  ["focusOrderValidated",              "Focus Order"],
  ["contrastValidated",                "Contrast"],
  ["textAlternativesValidated",        "Text Alt"],
  ["formsAccessibilityValidated",      "Forms"],
  ["responsiveAccessibilityValidated", "Responsive"],
  ["cognitiveAccessibilityValidated",  "Cognitive"],
];

const WCA_SIM_PRESETS = [
  { label: "Automated Accessibility Scan", type: "automated", tool: "axe-core" },
  { label: "Manual WCAG Review",           type: "manual",    tool: "Manual" },
  { label: "Keyboard Audit",               type: "keyboard",  tool: "Keyboard-only" },
  { label: "Full WCAG 2.2 AA Assessment",  type: "full",      tool: "Combined" },
];

function WCAAddEditDialog({ editing, canManage, onClose, onSaved }: {
  editing: WCAControl | null; canManage: boolean; onClose: () => void; onSaved: () => void;
}) {
  const { header } = useAuthWCA();
  const [form, setForm] = useState<Record<string, unknown>>({});
  useEffect(() => {
    setForm(editing ? { ...editing } : { environment: "Production", riskLevel: "High", status: "Draft" });
  }, [editing]);
  const set = (k: string, v: unknown) => setForm(p => ({ ...p, [k]: v }));
  const bool = (k: string) => !!form[k];
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState("");

  async function save() {
    const name = ((form.applicationName as string) ?? "").trim();
    const errs: Record<string, string> = {};
    required(errs, "applicationName", name, "Application Name");
    required(errs, "moduleName", form.moduleName as string, "Module / Component");
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({}); setApiError("");
    setSaving(true);
    try {
      const url = editing
        ? `/api/organization/compliance/wcag/compliance-assessment/${editing.id}`
        : `/api/organization/compliance/wcag/compliance-assessment`;
      const res = await fetch(url, {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const serverErrs = extractServerErrors(body);
        if (serverErrs) setErrors(serverErrs); else setApiError(apiErrMsg(res.status));
        return;
      }
      setErrors({}); onSaved(); onClose();
    } catch { setApiError("Unable to complete this action. Please try again."); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open onOpenChange={v => { if (!v) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit" : "Add"} WCAG 2.2 AA Compliance Assessment</DialogTitle>
          <DialogDescription>Metadata only — no user information, screenshots, or personal data stored.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Application Information */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Application Information</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium">Application Name *</label>
                <Input
                  className={fieldCls("", errors, "applicationName")}
                  value={(form.applicationName as string) ?? ""}
                  onChange={e => { set("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }}
                  aria-invalid={!!errors.applicationName}
                />
                {fErr(errors, "applicationName")}
              </div>
              <div><label className="text-xs font-medium">Module / Page *</label>
                <Input className={fieldCls("", errors, "moduleName")} aria-invalid={!!errors.moduleName} value={(form.moduleName as string) ?? ""} onChange={e => { set("moduleName", e.target.value); clearErr(setErrors, "moduleName"); }} />
                {fErr(errors, "moduleName")}</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-xs font-medium">Environment</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={(form.environment as string) ?? "Production"} onChange={e => set("environment", e.target.value)}>
                  {["Development","Staging","Production","All"].map(o => <option key={o}>{o}</option>)}
                </select></div>
              <div><label className="text-xs font-medium">Risk Level</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={(form.riskLevel as string) ?? "High"} onChange={e => set("riskLevel", e.target.value)}>
                  {["Low","Medium","High","Critical"].map(o => <option key={o}>{o}</option>)}
                </select></div>
              <div><label className="text-xs font-medium">Status</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={(form.status as string) ?? "Draft"} onChange={e => set("status", e.target.value)}>
                  {["Draft","Active","UnderReview","Approved","Archived"].map(o => <option key={o}>{o}</option>)}
                </select></div>
            </div>
          </div>

          {/* Assessment Details */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Assessment Details</h4>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-xs font-medium">Assessment Type</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={(form.assessmentType as string) ?? ""} onChange={e => set("assessmentType", e.target.value)}>
                  <option value="">Select…</option>
                  {["Manual","Automated","Combined"].map(o => <option key={o}>{o}</option>)}
                </select></div>
              <div><label className="text-xs font-medium">Assessor Name</label>
                <Input value={(form.assessorName as string) ?? ""} onChange={e => set("assessorName", e.target.value)} /></div>
              <div><label className="text-xs font-medium">Assessment Tool</label>
                <Input placeholder="axe-core, WAVE, Lighthouse…" value={(form.assessmentTool as string) ?? ""} onChange={e => set("assessmentTool", e.target.value)} /></div>
            </div>
            <div><label className="text-xs font-medium">Assessment Date</label>
              <Input type="date" value={(form.assessmentDate as string ?? "").split("T")[0]} onChange={e => set("assessmentDate", e.target.value)} /></div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={bool("manualTestingPerformed")} onChange={e => set("manualTestingPerformed", e.target.checked)} className="rounded" />
                Manual Testing Performed
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={bool("automatedTestingPerformed")} onChange={e => set("automatedTestingPerformed", e.target.checked)} className="rounded" />
                Automated Testing Performed
              </label>
            </div>
          </div>

          {/* WCAG Principle Validation */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">WCAG Principle Validation (POUR)</h4>
            <div className="grid grid-cols-2 gap-2">
              {WCA_PRINCIPLES.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={bool(k as string)} onChange={e => set(k as string, e.target.checked)} className="rounded" />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Success Criteria Checks */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">WCAG 2.2 Success Criteria</h4>
            <div className="grid grid-cols-2 gap-2">
              {WCA_CRITERIA.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={bool(k as string)} onChange={e => set(k as string, e.target.checked)} className="rounded" />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Findings & Evidence */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Findings &amp; Evidence</h4>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={bool("findingsExist")} onChange={e => set("findingsExist", e.target.checked)} className="rounded" />
              Findings Exist
            </label>
            {bool("findingsExist") && (
              <>
                <div><label className="text-xs font-medium">Findings Summary</label>
                  <Input placeholder="Brief description of findings" value={(form.findingsSummary as string) ?? ""} onChange={e => set("findingsSummary", e.target.value)} /></div>
                <div><label className="text-xs font-medium">Findings Reference</label>
                  <Input placeholder="Issue tracker link or document ID" value={(form.findingsReference as string) ?? ""} onChange={e => set("findingsReference", e.target.value)} /></div>
              </>
            )}
            <div><label className="text-xs font-medium">Evidence Reference</label>
              <Input placeholder="Audit report link, test results file, or document ID" value={(form.evidenceReference as string) ?? ""} onChange={e => set("evidenceReference", e.target.value)} /></div>
          </div>

          {/* Review Information */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Review Information</h4>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-medium">Reviewer</label>
                <Input value={(form.reviewer as string) ?? ""} onChange={e => set("reviewer", e.target.value)} /></div>
              <div><label className="text-xs font-medium">Next Review Date</label>
                <Input type="date" value={(form.nextReviewDate as string ?? "").split("T")[0]} onChange={e => set("nextReviewDate", e.target.value)} /></div>
            </div>
            <div><label className="text-xs font-medium">Review Notes</label>
              <Input value={(form.reviewNotes as string) ?? ""} onChange={e => set("reviewNotes", e.target.value)} /></div>
          </div>
        </div>
        {apiError && <p role="alert" className="text-xs text-red-500 mt-2 flex items-center gap-1"><span aria-hidden="true">⚠</span> {apiError}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {canManage && <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WCAValidateDialog({ control, onClose, onDone }: { control: WCAControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthWCA();
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  async function run() {
    setLoading(true); setApiError("");
    try {
      const res = await fetch(`/api/organization/compliance/wcag/compliance-assessment/${control.id}/validate`, { method: "POST", headers: header });
      if (!res.ok) { setApiError(apiErrMsg(res.status)); return; }
      setResult(await res.json());
      onDone();
    } catch { setApiError("Unable to complete this action. Please try again."); }
    finally { setLoading(false); }
  }
  const codes = (result?.validationCodes ?? control.validationCodes) as string[];
  const guidance = result?.remediationGuidance as string[] | undefined;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Validate — {control.applicationName}</DialogTitle>
          <DialogDescription>Runs all 14 WCAG 2.2 AA compliance checks.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">POUR Principles</p>
            <div className="grid grid-cols-2 gap-1 text-sm">
              {WCA_PRINCIPLES.map(([k, label]) => (
                <div key={k} className="flex items-center gap-1">
                  <span>{control[k as keyof WCAControl] ? "✓" : "✗"}</span>
                  <span className={control[k as keyof WCAControl] ? "text-green-700" : "text-red-600"}>{label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Success Criteria</p>
            <div className="grid grid-cols-2 gap-1 text-sm">
              {WCA_CRITERIA.map(([k, label]) => (
                <div key={k} className="flex items-center gap-1">
                  <span>{control[k as keyof WCAControl] ? "✓" : "✗"}</span>
                  <span className={control[k as keyof WCAControl] ? "text-green-700" : "text-red-600"}>{label}</span>
                </div>
              ))}
            </div>
          </div>
          {result && (
            <div className={`rounded p-2 text-sm font-medium ${result.isCompliant ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
              {result.isCompliant ? "✓ All checks passed" : `✗ ${codes.length} issue(s) found`}
            </div>
          )}
          {codes.length > 0 && (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {codes.map((c, i) => (
                <div key={c} className="text-xs bg-red-50 rounded p-1">
                  <span className="font-mono font-semibold text-red-600">{c}</span>
                  {guidance?.[i] && <p className="mt-0.5 text-gray-600">{guidance[i]}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
        {apiError && <p role="alert" className="text-xs text-red-500 mt-1 flex items-center gap-1"><span aria-hidden="true">⚠</span> {apiError}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={run} disabled={loading}>{loading ? "Validating…" : "Run Validation"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WCASimDialog({ control, onClose, onDone }: { control: WCAControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthWCA();
  const [preset, setPreset] = useState(0);
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  async function run() {
    setLoading(true); setApiError("");
    const p = WCA_SIM_PRESETS[preset];
    try {
      const res = await fetch(`/api/organization/compliance/wcag/compliance-assessment/${control.id}/assessment-simulation`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ simulationType: p.type, assessmentTool: p.tool }),
      });
      if (!res.ok) { setApiError(apiErrMsg(res.status)); return; }
      setResult(await res.json());
      onDone();
    } catch { setApiError("Unable to complete this action. Please try again."); }
    finally { setLoading(false); }
  }
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assessment Simulation — {control.applicationName}</DialogTitle>
          <DialogDescription className="text-amber-700 font-medium">Simulation only — no live accessibility audit executed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {WCA_SIM_PRESETS.map((p, i) => (
              <button key={i} onClick={() => setPreset(i)}
                className={`rounded border p-2 text-sm text-left transition-colors ${preset === i ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:bg-gray-50"}`}>
                {p.label}
              </button>
            ))}
          </div>
          {result && (
            <div className="space-y-2">
              <div className={`rounded p-2 text-sm font-medium ${result.passed ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                {result.passed ? "✓ Simulation passed" : "⚠ Issues found"}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ["POUR Principles", result.principlesResult],
                  ["Success Criteria", result.criteriaResult],
                  ["Findings", result.findingsResult],
                  ["Evidence", result.evidenceResult],
                ].map(([label, ok]) => (
                  <div key={label as string} className={`rounded p-1 flex items-center gap-1 ${ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                    <span>{ok ? "✓" : "✗"}</span><span>{label as string}</span>
                  </div>
                ))}
              </div>
              {(result.failedCriteria as string[])?.length > 0 && (
                <ul className="text-xs text-red-600 list-disc pl-4 space-y-0.5">
                  {(result.failedCriteria as string[]).map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              )}
              <p className="text-xs text-gray-500 italic">{result.note as string}</p>
            </div>
          )}
        </div>
        {apiError && <p role="alert" className="text-xs text-red-500 mt-1 flex items-center gap-1"><span aria-hidden="true">⚠</span> {apiError}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={run} disabled={loading}>{loading ? "Running…" : "Run Simulation"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WCAReviewDialog({ control, onClose, onDone }: { control: WCAControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthWCA();
  const [reviewer, setReviewer] = useState("");
  const [notes, setNotes]       = useState("");
  const [nextDate, setNextDate]  = useState("");
  const [saving, setSaving]      = useState(false);
  const [apiError, setApiError] = useState("");
  async function save() {
    if (!reviewer.trim()) return;
    setSaving(true); setApiError("");
    try {
      const res = await fetch(`/api/organization/compliance/wcag/compliance-assessment/${control.id}/review`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ reviewerName: reviewer, reviewNotes: notes, nextReviewDate: nextDate || null }),
      });
      if (!res.ok) { setApiError(apiErrMsg(res.status)); return; }
      onDone(); onClose();
    } catch { setApiError("Unable to complete this action. Please try again."); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Review — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Reviewer *</label>
            <Input value={reviewer} onChange={e => setReviewer(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Next Review Date</label>
            <Input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Notes</label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} /></div>
        </div>
        {apiError && <p role="alert" className="text-xs text-red-500 mt-1 flex items-center gap-1"><span aria-hidden="true">⚠</span> {apiError}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !reviewer.trim()}>{saving ? "Saving…" : "Save Review"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WCARemediationDialog({ control, onClose, onDone }: { control: WCAControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthWCA();
  const [notes, setNotes]       = useState("");
  const [findingsFix, setFix]   = useState("");
  const [completion, setCompletion] = useState("");
  const [saving, setSaving]      = useState(false);
  const [apiError, setApiError] = useState("");
  async function save() {
    if (!notes.trim()) return;
    setSaving(true); setApiError("");
    try {
      const res = await fetch(`/api/organization/compliance/wcag/compliance-assessment/${control.id}/remediation`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ remediationNotes: notes, findingsFix, completionDate: completion || null }),
      });
      if (!res.ok) { setApiError(apiErrMsg(res.status)); return; }
      onDone(); onClose();
    } catch { setApiError("Unable to complete this action. Please try again."); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Remediation — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Remediation Notes *</label>
            <Input placeholder="Describe the remediation plan" value={notes} onChange={e => setNotes(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Fix Notes</label>
            <Input placeholder="Specific findings to fix" value={findingsFix} onChange={e => setFix(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Target Completion Date</label>
            <Input type="date" value={completion} onChange={e => setCompletion(e.target.value)} /></div>
        </div>
        {apiError && <p role="alert" className="text-xs text-red-500 mt-1 flex items-center gap-1"><span aria-hidden="true">⚠</span> {apiError}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !notes.trim()}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WCADeleteDialog({ control, onClose, onDone }: { control: WCAControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthWCA();
  const [deleting, setDeleting] = useState(false);
  const [apiError, setApiError] = useState("");
  async function del() {
    setDeleting(true); setApiError("");
    try {
      const res = await fetch(`/api/organization/compliance/wcag/compliance-assessment/${control.id}`, { method: "DELETE", headers: header });
      if (!res.ok) { setApiError(apiErrMsg(res.status)); return; }
      onDone(); onClose();
    } catch { setApiError("Unable to complete this action. Please try again."); }
    finally { setDeleting(false); }
  }
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Delete Assessment</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Delete <strong>{control.applicationName}</strong>? This cannot be undone.</p>
        {apiError && <p role="alert" className="text-xs text-red-500 mt-1 flex items-center gap-1"><span aria-hidden="true">⚠</span> {apiError}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={del} disabled={deleting}>{deleting ? "Deleting…" : "Delete"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function WcagComplianceAssessmentSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuthWCA();
  const qc = useQueryClient();
  const [addOpen, setAddOpen]         = useState(false);
  const [editing, setEditing]         = useState<WCAControl | null>(null);
  const [validating, setValidating]   = useState<WCAControl | null>(null);
  const [simming, setSimming]         = useState<WCAControl | null>(null);
  const [reviewing, setReviewing]     = useState<WCAControl | null>(null);
  const [remediating, setRemediating] = useState<WCAControl | null>(null);
  const [deleting, setDeleting]       = useState<WCAControl | null>(null);

  const [fApp, setFApp]     = useState("");
  const [fEnv, setFEnv]     = useState("");
  const [fType, setFType]   = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fRisk, setFRisk]   = useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["wca"] });

  const summaryQ = useQuery<WCASummary>({
    queryKey: ["wca", "summary"],
    queryFn: async () => {
      const r = await fetch("/api/organization/compliance/wcag/compliance-assessment/summary", { headers: header });
      return r.json();
    },
  });
  const listQ = useQuery<WCAControl[]>({
    queryKey: ["wca", "list", fApp, fEnv, fType, fStatus, fRisk],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (fApp)    p.set("applicationName", fApp);
      if (fEnv)    p.set("environment", fEnv);
      if (fType)   p.set("assessmentType", fType);
      if (fStatus) p.set("complianceStatus", fStatus);
      if (fRisk)   p.set("riskLevel", fRisk);
      const r = await fetch(`/api/organization/compliance/wcag/compliance-assessment?${p}`, { headers: header });
      return r.json();
    },
  });
  const s = summaryQ.data;
  const list = listQ.data ?? [];

  const compBadge = (s: string) => {
    const cls = s === "Compliant" ? "bg-green-100 text-green-700" : s === "NonCompliant" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600";
    return <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${cls}`}>{s}</span>;
  };
  const riskBadge = (r: string) => {
    const cls = r === "Critical" ? "bg-red-100 text-red-700" : r === "High" ? "bg-orange-100 text-orange-700" : r === "Medium" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700";
    return <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${cls}`}>{r}</span>;
  };

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">WCAG 2.2 AA Compliance Assessment</h3>
          <p className="text-xs text-muted-foreground">Accessibility assessment records and compliance evidence — metadata only</p>
        </div>
        {canManage && <Button size="sm" onClick={() => setAddOpen(true)}>+ Add</Button>}
      </div>

      {/* Summary */}
      {s && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {[
            ["Total Applications", s.totalApplications],
            ["Fully Compliant",    s.fullyCompliant],
            ["Assessments Done",   s.assessmentsCompleted],
            ["Findings Open",      s.findingsOpen],
            ["Evidence Missing",   s.evidenceMissing],
            ["Reviews Overdue",    s.reviewsOverdue],
          ].map(([label, val]) => (
            <div key={label as string} className="rounded border p-2 text-center">
              <div className="text-lg font-bold">{val as number}</div>
              <div className="text-xs text-muted-foreground leading-tight">{label as string}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 text-xs">
        <Input placeholder="Application…" value={fApp} onChange={e => setFApp(e.target.value)} className="h-7 text-xs" />
        <select className="border rounded px-2 py-1 text-xs h-7" value={fEnv} onChange={e => setFEnv(e.target.value)}>
          <option value="">All Environments</option>
          {["Development","Staging","Production","All"].map(o => <option key={o}>{o}</option>)}
        </select>
        <select className="border rounded px-2 py-1 text-xs h-7" value={fType} onChange={e => setFType(e.target.value)}>
          <option value="">All Types</option>
          {["Manual","Automated","Combined"].map(o => <option key={o}>{o}</option>)}
        </select>
        <select className="border rounded px-2 py-1 text-xs h-7" value={fStatus} onChange={e => setFStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {["Compliant","NonCompliant","Pending","UnderReview"].map(o => <option key={o}>{o}</option>)}
        </select>
        <select className="border rounded px-2 py-1 text-xs h-7" value={fRisk} onChange={e => setFRisk(e.target.value)}>
          <option value="">All Risk Levels</option>
          {["Low","Medium","High","Critical"].map(o => <option key={o}>{o}</option>)}
        </select>
      </div>

      {/* Control List */}
      <div className="space-y-2">
        {list.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No assessments found.</p>}
        {list.map(c => (
          <div key={c.id} className="border rounded p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{c.applicationName}</span>
                  {c.moduleName && <span className="text-xs text-muted-foreground">({c.moduleName})</span>}
                  <span className="text-xs bg-gray-100 text-gray-600 rounded px-1">{c.environment}</span>
                  {c.assessmentType && <span className="text-xs bg-blue-50 text-blue-700 rounded px-1">{c.assessmentType}</span>}
                  {riskBadge(c.riskLevel)}
                  {compBadge(c.complianceStatus)}
                  {c.reviewOverdue && <span className="text-xs text-orange-600 font-medium">⏰ Overdue</span>}
                </div>
                <div className="text-xs text-muted-foreground flex gap-3 flex-wrap">
                  {c.assessorName && <span>Assessor: {c.assessorName}</span>}
                  {c.assessmentTool && <span>Tool: {c.assessmentTool}</span>}
                  {c.assessmentDate && <span>Date: {c.assessmentDate.split("T")[0]}</span>}
                  {c.findingsExist && <span className="text-amber-600 font-medium">⚠ Findings exist</span>}
                  {c.evidenceReference ? <span className="text-green-600">✓ Evidence</span> : <span className="text-red-500">✗ No evidence</span>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setValidating(c)}>Validate</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSimming(c)}>Simulate</Button>
                {canManage && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setReviewing(c)}>Review</Button>}
                {canManage && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRemediating(c)}>Remediate</Button>}
                {canManage && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing(c); setAddOpen(true); }}>Edit</Button>}
                {canManage && <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => setDeleting(c)}>Delete</Button>}
              </div>
            </div>

            {/* POUR principles grid */}
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">POUR Principles</p>
              <div className="grid grid-cols-4 gap-1 text-xs">
                {WCA_PRINCIPLES.map(([k, label]) => (
                  <div key={k} className={`rounded p-1 text-center ${c[k as keyof WCAControl] ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                    <div>{c[k as keyof WCAControl] ? "✓" : "✗"}</div>
                    <div className="text-[10px]">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Success criteria grid */}
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Success Criteria</p>
              <div className="grid grid-cols-4 gap-1 text-xs">
                {WCA_CRITERIA.map(([k, label]) => (
                  <div key={k} className={`rounded p-1 text-center ${c[k as keyof WCAControl] ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                    <div>{c[k as keyof WCAControl] ? "✓" : "✗"}</div>
                    <div className="text-[10px] leading-tight">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Validation codes */}
            {c.validationCodes?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {c.validationCodes.slice(0, 4).map(code => (
                  <span key={code} className="text-[10px] bg-red-50 text-red-700 rounded px-1 py-0.5 font-mono">{code}</span>
                ))}
                {c.validationCodes.length > 4 && <span className="text-[10px] text-muted-foreground">+{c.validationCodes.length - 4} more</span>}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Dialogs */}
      {(addOpen || editing) && (
        <WCAAddEditDialog editing={editing} canManage={canManage} onClose={() => { setAddOpen(false); setEditing(null); }} onSaved={invalidate} />
      )}
      {validating  && <WCAValidateDialog   control={validating}  onClose={() => setValidating(null)}  onDone={invalidate} />}
      {simming     && <WCASimDialog        control={simming}     onClose={() => setSimming(null)}      onDone={invalidate} />}
      {reviewing   && <WCAReviewDialog     control={reviewing}   onClose={() => setReviewing(null)}   onDone={invalidate} />}
      {remediating && <WCARemediationDialog control={remediating} onClose={() => setRemediating(null)} onDone={invalidate} />}
      {deleting    && <WCADeleteDialog     control={deleting}    onClose={() => setDeleting(null)}    onDone={invalidate} />}
    </Card>
  );
}

// ── Screen Reader Validation ──────────────────────────────────────────────────

interface SRVControl {
  id: string; applicationName: string; moduleName?: string; environment: string;
  screenReaderTool?: string; browser?: string; operatingSystem?: string;
  representativeWorkflow?: string; workflowCoverageDescription?: string;
  navigationLandmarksValidated: boolean; headingStructureValidated: boolean;
  ariaAnnouncementsValidated: boolean; dynamicContentValidated: boolean;
  keyboardWithScreenReaderValidated: boolean; formInteractionValidated: boolean;
  testingCompleted: boolean; testerName?: string; testDate?: string;
  testEvidenceReference?: string;
  complianceStatus: string; riskLevel: string; status: string;
  remediationNotes?: string; reviewer?: string; reviewNotes?: string;
  lastReviewDate?: string; nextReviewDate?: string; reviewOverdue: boolean;
  validationCodes: string[];
}
interface SRVSummary {
  totalApplications: number; fullyValidated: number; screenReaderTested: number;
  workflowCoverage: number; missingEvidence: number; reviewOverdue: number;
}

function useAuthSRV() {
  const token = localStorage.getItem("dotnetbot.jwt") ?? sessionStorage.getItem("dotnetbot.jwt") ?? "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

const SRV_CHECKS: [keyof SRVControl, string][] = [
  ["navigationLandmarksValidated", "Navigation Landmarks"],
  ["headingStructureValidated", "Heading Structure"],
  ["ariaAnnouncementsValidated", "ARIA Announcements"],
  ["dynamicContentValidated", "Dynamic Content"],
  ["keyboardWithScreenReaderValidated", "Keyboard + SR"],
  ["formInteractionValidated", "Form Interaction"],
];

const SRV_SIM_PRESETS = [
  { label: "NVDA Workflow Test", tool: "NVDA", type: "workflow" },
  { label: "JAWS Navigation Test", tool: "JAWS", type: "navigation" },
  { label: "VoiceOver Mobile Test", tool: "VoiceOver", type: "mobile" },
  { label: "Full Assistive Technology Audit", tool: "", type: "full" },
];

function SRVAddEditDialog({ editing, canManage, onClose, onSaved }: {
  editing: SRVControl | null; canManage: boolean; onClose: () => void; onSaved: () => void;
}) {
  const { header } = useAuthSRV();
  const [form, setForm] = useState<Record<string, unknown>>({});
  useEffect(() => {
    setForm(editing ? { ...editing } : {
      environment: "Production", riskLevel: "High", status: "Draft",
    });
  }, [editing]);
  const set = (k: string, v: unknown) => setForm(p => ({ ...p, [k]: v }));
  const bool = (k: string) => !!form[k];
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState("");

  async function save() {
    const name = ((form.applicationName as string) ?? "").trim();
    const errs: Record<string, string> = {};
    required(errs, "applicationName", name, "Application Name");
    required(errs, "moduleName", form.moduleName as string, "Module / Component");
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({}); setApiError("");
    setSaving(true);
    try {
      const url = editing
        ? `/api/organization/compliance/wcag/screen-reader-validation/${editing.id}`
        : `/api/organization/compliance/wcag/screen-reader-validation`;
      const res = await fetch(url, {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const serverErrs = extractServerErrors(body);
        if (serverErrs) setErrors(serverErrs); else setApiError(apiErrMsg(res.status));
        return;
      }
      setErrors({}); onSaved(); onClose();
    } catch { setApiError("Unable to complete this action. Please try again."); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={v => { if (!v) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit" : "Add"} Screen Reader Validation</DialogTitle>
          <DialogDescription>Metadata only — no screen recordings, audio, or user data stored.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Application Information */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Application Information</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium">Application Name *</label>
                <Input
                  className={fieldCls("", errors, "applicationName")}
                  value={(form.applicationName as string) ?? ""}
                  onChange={e => { set("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }}
                  aria-invalid={!!errors.applicationName}
                />
                {fErr(errors, "applicationName")}
              </div>
              <div><label className="text-xs font-medium">Module / Page *</label>
                <Input className={fieldCls("", errors, "moduleName")} aria-invalid={!!errors.moduleName} value={(form.moduleName as string) ?? ""} onChange={e => { set("moduleName", e.target.value); clearErr(setErrors, "moduleName"); }} />
                {fErr(errors, "moduleName")}</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-xs font-medium">Environment</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={(form.environment as string) ?? "Production"} onChange={e => set("environment", e.target.value)}>
                  {["Development","Staging","Production","All"].map(o => <option key={o}>{o}</option>)}
                </select></div>
              <div><label className="text-xs font-medium">Risk Level</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={(form.riskLevel as string) ?? "High"} onChange={e => set("riskLevel", e.target.value)}>
                  {["Low","Medium","High","Critical"].map(o => <option key={o}>{o}</option>)}
                </select></div>
              <div><label className="text-xs font-medium">Status</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={(form.status as string) ?? "Draft"} onChange={e => set("status", e.target.value)}>
                  {["Draft","Active","UnderReview","Approved","Archived"].map(o => <option key={o}>{o}</option>)}
                </select></div>
            </div>
          </div>

          {/* Assistive Technology Details */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Assistive Technology Details</h4>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-xs font-medium">Screen Reader Tool</label>
                <Input placeholder="NVDA, JAWS, VoiceOver…" value={(form.screenReaderTool as string) ?? ""} onChange={e => set("screenReaderTool", e.target.value)} /></div>
              <div><label className="text-xs font-medium">Browser</label>
                <Input placeholder="Chrome, Firefox…" value={(form.browser as string) ?? ""} onChange={e => set("browser", e.target.value)} /></div>
              <div><label className="text-xs font-medium">Operating System</label>
                <Input placeholder="Windows, macOS…" value={(form.operatingSystem as string) ?? ""} onChange={e => set("operatingSystem", e.target.value)} /></div>
            </div>
            <div><label className="text-xs font-medium">Representative Workflow</label>
              <Input placeholder="e.g. Login and navigate to dashboard" value={(form.representativeWorkflow as string) ?? ""} onChange={e => set("representativeWorkflow", e.target.value)} /></div>
            <div><label className="text-xs font-medium">Workflow Coverage Description</label>
              <Input placeholder="Describe what was covered in the workflow test" value={(form.workflowCoverageDescription as string) ?? ""} onChange={e => set("workflowCoverageDescription", e.target.value)} /></div>
          </div>

          {/* Validation Checklist */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Validation Checklist</h4>
            <div className="grid grid-cols-2 gap-2">
              {SRV_CHECKS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={bool(k as string)} onChange={e => set(k as string, e.target.checked)} className="rounded" />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Testing Evidence */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Testing Evidence</h4>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-medium">Tester Name</label>
                <Input value={(form.testerName as string) ?? ""} onChange={e => set("testerName", e.target.value)} /></div>
              <div><label className="text-xs font-medium">Test Date</label>
                <Input type="date" value={(form.testDate as string ?? "").split("T")[0]} onChange={e => set("testDate", e.target.value)} /></div>
            </div>
            <div><label className="text-xs font-medium">Evidence Reference</label>
              <Input placeholder="JIRA-123, report URL, or document ID" value={(form.testEvidenceReference as string) ?? ""} onChange={e => set("testEvidenceReference", e.target.value)} /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={bool("testingCompleted")} onChange={e => set("testingCompleted", e.target.checked)} className="rounded" />
              Testing Completed
            </label>
          </div>

          {/* Review Information */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Review Information</h4>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-medium">Reviewer</label>
                <Input value={(form.reviewer as string) ?? ""} onChange={e => set("reviewer", e.target.value)} /></div>
              <div><label className="text-xs font-medium">Next Review Date</label>
                <Input type="date" value={(form.nextReviewDate as string ?? "").split("T")[0]} onChange={e => set("nextReviewDate", e.target.value)} /></div>
            </div>
            <div><label className="text-xs font-medium">Review Notes</label>
              <Input value={(form.reviewNotes as string) ?? ""} onChange={e => set("reviewNotes", e.target.value)} /></div>
          </div>
        </div>
        {apiError && <p role="alert" className="text-xs text-red-500 mt-2 flex items-center gap-1"><span aria-hidden="true">⚠</span> {apiError}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {canManage && <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SRVValidateDialog({ control, onClose, onDone }: { control: SRVControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthSRV();
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  async function run() {
    setLoading(true); setApiError("");
    try {
      const res = await fetch(`/api/organization/compliance/wcag/screen-reader-validation/${control.id}/validate`, { method: "POST", headers: header });
      if (!res.ok) { setApiError(apiErrMsg(res.status)); return; }
      setResult(await res.json());
      onDone();
    } catch { setApiError("Unable to complete this action. Please try again."); }
    finally { setLoading(false); }
  }
  const codes = (result?.validationCodes ?? control.validationCodes) as string[];
  const suggestions = result?.remediationSuggestions as string[] | undefined;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Validate — {control.applicationName}</DialogTitle>
          <DialogDescription>Runs all 11 screen reader validation checks.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 text-sm">
            {SRV_CHECKS.map(([k, label]) => (
              <div key={k} className="flex items-center gap-1">
                <span>{control[k as keyof SRVControl] ? "✓" : "✗"}</span>
                <span className={control[k as keyof SRVControl] ? "text-green-700" : "text-red-600"}>{label}</span>
              </div>
            ))}
          </div>
          {result && (
            <div className={`rounded p-2 text-sm font-medium ${result.isCompliant ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
              {result.isCompliant ? "✓ All checks passed" : `✗ ${codes.length} issue(s) found`}
            </div>
          )}
          {codes.length > 0 && (
            <div className="space-y-1">
              {codes.map((c, i) => (
                <div key={c} className="text-xs text-red-600 bg-red-50 rounded p-1">
                  <span className="font-mono font-semibold">{c}</span>
                  {suggestions?.[i] && <p className="mt-0.5 text-gray-600">{suggestions[i]}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
        {apiError && <p role="alert" className="text-xs text-red-500 mt-1 flex items-center gap-1"><span aria-hidden="true">⚠</span> {apiError}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={run} disabled={loading}>{loading ? "Validating…" : "Run Validation"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SRVTestSimDialog({ control, onClose, onDone }: { control: SRVControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthSRV();
  const [preset, setPreset] = useState(0);
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  async function run() {
    setLoading(true); setApiError("");
    const p = SRV_SIM_PRESETS[preset];
    try {
      const res = await fetch(`/api/organization/compliance/wcag/screen-reader-validation/${control.id}/test-simulation`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ testType: p.type, screenReaderTool: p.tool }),
      });
      if (!res.ok) { setApiError(apiErrMsg(res.status)); return; }
      setResult(await res.json());
      onDone();
    } catch { setApiError("Unable to complete this action. Please try again."); }
    finally { setLoading(false); }
  }
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Test Simulation — {control.applicationName}</DialogTitle>
          <DialogDescription className="text-amber-700 font-medium">Simulation only — no real assistive technology session executed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {SRV_SIM_PRESETS.map((p, i) => (
              <button key={i} onClick={() => setPreset(i)}
                className={`rounded border p-2 text-sm text-left transition-colors ${preset === i ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:bg-gray-50"}`}>
                {p.label}
              </button>
            ))}
          </div>
          {result && (
            <div className="space-y-2">
              <div className={`rounded p-2 text-sm font-medium ${result.passed ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                {result.passed ? "✓ Simulation passed" : "⚠ Issues found"}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ["Screen Reader", result.screenReaderResult],
                  ["Workflow", result.workflowResult],
                  ["ARIA", result.ariaResult],
                  ["Keyboard", result.keyboardResult],
                ].map(([label, ok]) => (
                  <div key={label as string} className={`rounded p-1 flex items-center gap-1 ${ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                    <span>{ok ? "✓" : "✗"}</span><span>{label as string}</span>
                  </div>
                ))}
              </div>
              {(result.accessibilityIssues as string[])?.length > 0 && (
                <ul className="text-xs text-red-600 list-disc pl-4 space-y-0.5">
                  {(result.accessibilityIssues as string[]).map((issue, i) => <li key={i}>{issue}</li>)}
                </ul>
              )}
              <p className="text-xs text-gray-500 italic">{result.note as string}</p>
            </div>
          )}
        </div>
        {apiError && <p role="alert" className="text-xs text-red-500 mt-1 flex items-center gap-1"><span aria-hidden="true">⚠</span> {apiError}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={run} disabled={loading}>{loading ? "Running…" : "Run Simulation"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SRVReviewDialog({ control, onClose, onDone }: { control: SRVControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthSRV();
  const [reviewer, setReviewer] = useState("");
  const [notes, setNotes]     = useState("");
  const [nextDate, setNextDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");
  async function save() {
    if (!reviewer.trim()) return;
    setSaving(true); setApiError("");
    try {
      const res = await fetch(`/api/organization/compliance/wcag/screen-reader-validation/${control.id}/review`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ reviewerName: reviewer, reviewNotes: notes, nextReviewDate: nextDate || null }),
      });
      if (!res.ok) { setApiError(apiErrMsg(res.status)); return; }
      onDone(); onClose();
    } catch { setApiError("Unable to complete this action. Please try again."); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Review — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Reviewer *</label>
            <Input value={reviewer} onChange={e => setReviewer(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Next Review Date</label>
            <Input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Notes</label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} /></div>
        </div>
        {apiError && <p role="alert" className="text-xs text-red-500 mt-1 flex items-center gap-1"><span aria-hidden="true">⚠</span> {apiError}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !reviewer.trim()}>{saving ? "Saving…" : "Save Review"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SRVDeleteDialog({ control, onClose, onDone }: { control: SRVControl; onClose: () => void; onDone: () => void }) {
  const { header } = useAuthSRV();
  const [deleting, setDeleting] = useState(false);
  const [apiError, setApiError] = useState("");
  async function del() {
    setDeleting(true); setApiError("");
    try {
      const res = await fetch(`/api/organization/compliance/wcag/screen-reader-validation/${control.id}`, { method: "DELETE", headers: header });
      if (!res.ok) { setApiError(apiErrMsg(res.status)); return; }
      onDone(); onClose();
    } catch { setApiError("Unable to complete this action. Please try again."); }
    finally { setDeleting(false); }
  }
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Delete Screen Reader Validation</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Delete <strong>{control.applicationName}</strong>? This cannot be undone.</p>
        {apiError && <p role="alert" className="text-xs text-red-500 mt-1 flex items-center gap-1"><span aria-hidden="true">⚠</span> {apiError}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={del} disabled={deleting}>{deleting ? "Deleting…" : "Delete"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ScreenReaderValidationSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuthSRV();
  const qc = useQueryClient();
  const [addOpen, setAddOpen]     = useState(false);
  const [editing, setEditing]     = useState<SRVControl | null>(null);
  const [validating, setValidating] = useState<SRVControl | null>(null);
  const [testing, setTesting]     = useState<SRVControl | null>(null);
  const [reviewing, setReviewing] = useState<SRVControl | null>(null);
  const [deleting, setDeleting]   = useState<SRVControl | null>(null);

  // Filters
  const [fApp, setFApp] = useState("");
  const [fEnv, setFEnv] = useState("");
  const [fTool, setFTool] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fRisk, setFRisk] = useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["srv"] });

  const summaryQ = useQuery<SRVSummary>({
    queryKey: ["srv", "summary"],
    queryFn: async () => {
      const r = await fetch("/api/organization/compliance/wcag/screen-reader-validation/summary", { headers: header });
      return r.json();
    },
  });
  const listQ = useQuery<SRVControl[]>({
    queryKey: ["srv", "list", fApp, fEnv, fTool, fStatus, fRisk],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (fApp) p.set("applicationName", fApp);
      if (fEnv) p.set("environment", fEnv);
      if (fTool) p.set("screenReaderTool", fTool);
      if (fStatus) p.set("complianceStatus", fStatus);
      if (fRisk) p.set("riskLevel", fRisk);
      const r = await fetch(`/api/organization/compliance/wcag/screen-reader-validation?${p}`, { headers: header });
      return r.json();
    },
  });
  const s = summaryQ.data;
  const list = listQ.data ?? [];

  const compBadge = (s: string) => {
    const cls = s === "Compliant" ? "bg-green-100 text-green-700" : s === "NonCompliant" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600";
    return <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${cls}`}>{s}</span>;
  };
  const riskBadge = (r: string) => {
    const cls = r === "Critical" ? "bg-red-100 text-red-700" : r === "High" ? "bg-orange-100 text-orange-700" : r === "Medium" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700";
    return <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${cls}`}>{r}</span>;
  };

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">Screen Reader Validation</h3>
          <p className="text-xs text-muted-foreground">Assistive technology workflow testing — metadata only</p>
        </div>
        {canManage && <Button size="sm" onClick={() => setAddOpen(true)}>+ Add</Button>}
      </div>

      {/* Summary */}
      {s && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {[
            ["Total Applications", s.totalApplications],
            ["Fully Validated", s.fullyValidated],
            ["SR Tested", s.screenReaderTested],
            ["Workflow Coverage", s.workflowCoverage],
            ["Missing Evidence", s.missingEvidence],
            ["Review Overdue", s.reviewOverdue],
          ].map(([label, val]) => (
            <div key={label as string} className="rounded border p-2 text-center">
              <div className="text-lg font-bold">{val as number}</div>
              <div className="text-xs text-muted-foreground">{label as string}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 text-xs">
        <Input placeholder="Application…" value={fApp} onChange={e => setFApp(e.target.value)} className="h-7 text-xs" />
        <select className="border rounded px-2 py-1 text-xs h-7" value={fEnv} onChange={e => setFEnv(e.target.value)}>
          <option value="">All Environments</option>
          {["Development","Staging","Production","All"].map(o => <option key={o}>{o}</option>)}
        </select>
        <Input placeholder="Screen reader tool…" value={fTool} onChange={e => setFTool(e.target.value)} className="h-7 text-xs" />
        <select className="border rounded px-2 py-1 text-xs h-7" value={fStatus} onChange={e => setFStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {["Compliant","NonCompliant","Pending","UnderReview"].map(o => <option key={o}>{o}</option>)}
        </select>
        <select className="border rounded px-2 py-1 text-xs h-7" value={fRisk} onChange={e => setFRisk(e.target.value)}>
          <option value="">All Risk Levels</option>
          {["Low","Medium","High","Critical"].map(o => <option key={o}>{o}</option>)}
        </select>
      </div>

      {/* Control List */}
      <div className="space-y-2">
        {list.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No screen reader validation controls found.</p>}
        {list.map(c => (
          <div key={c.id} className="border rounded p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{c.applicationName}</span>
                  {c.moduleName && <span className="text-xs text-muted-foreground">({c.moduleName})</span>}
                  <span className="text-xs bg-gray-100 text-gray-600 rounded px-1">{c.environment}</span>
                  {riskBadge(c.riskLevel)}
                  {compBadge(c.complianceStatus)}
                  {c.reviewOverdue && <span className="text-xs text-orange-600 font-medium">⏰ Review Overdue</span>}
                </div>
                <div className="text-xs text-muted-foreground flex gap-3 flex-wrap">
                  {c.screenReaderTool && <span>Tool: {c.screenReaderTool}</span>}
                  {c.testerName && <span>Tester: {c.testerName}</span>}
                  {c.representativeWorkflow && <span className="italic">"{c.representativeWorkflow.slice(0, 60)}{c.representativeWorkflow.length > 60 ? "…" : ""}"</span>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setValidating(c)}>Validate</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setTesting(c)}>Simulate</Button>
                {canManage && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setReviewing(c)}>Review</Button>}
                {canManage && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing(c); setAddOpen(true); }}>Edit</Button>}
                {canManage && <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => setDeleting(c)}>Delete</Button>}
              </div>
            </div>

            {/* 6-check grid */}
            <div className="grid grid-cols-3 gap-1 text-xs sm:grid-cols-6">
              {SRV_CHECKS.map(([k, label]) => (
                <div key={k} className={`rounded p-1 text-center ${c[k as keyof SRVControl] ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                  <div>{c[k as keyof SRVControl] ? "✓" : "✗"}</div>
                  <div className="text-[10px]">{label}</div>
                </div>
              ))}
            </div>

            {/* Evidence + validation codes */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
              <span>{c.testingCompleted ? "✓ Tested" : "✗ Not Tested"}</span>
              {c.testEvidenceReference && <span>Evidence: {c.testEvidenceReference}</span>}
              {c.testDate && <span>Test date: {c.testDate.split("T")[0]}</span>}
              {c.nextReviewDate && <span>Next review: {c.nextReviewDate.split("T")[0]}</span>}
            </div>
            {c.validationCodes?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {c.validationCodes.slice(0, 4).map(code => (
                  <span key={code} className="text-[10px] bg-red-50 text-red-700 rounded px-1 py-0.5 font-mono">{code}</span>
                ))}
                {c.validationCodes.length > 4 && (
                  <span className="text-[10px] text-muted-foreground">+{c.validationCodes.length - 4} more</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Dialogs */}
      {(addOpen || editing) && (
        <SRVAddEditDialog editing={editing} canManage={canManage} onClose={() => { setAddOpen(false); setEditing(null); }} onSaved={invalidate} />
      )}
      {validating && <SRVValidateDialog control={validating} onClose={() => setValidating(null)} onDone={invalidate} />}
      {testing   && <SRVTestSimDialog   control={testing}    onClose={() => setTesting(null)}    onDone={invalidate} />}
      {reviewing && <SRVReviewDialog    control={reviewing}  onClose={() => setReviewing(null)}  onDone={invalidate} />}
      {deleting  && <SRVDeleteDialog    control={deleting}   onClose={() => setDeleting(null)}   onDone={invalidate} />}
    </Card>
  );
}

// ── WcagTab root ──────────────────────────────────────────────────────────────
export function WcagTab({ canManage }: { canManage: boolean }) {
  return (
    <div className="space-y-6 pt-4">
      <WcagComplianceAssessmentSection canManage={canManage} />
      <ScreenReaderValidationSection canManage={canManage} />
      <AccessibleFormsSection canManage={canManage} />
      <ChatbotAccessibilitySection canManage={canManage} />
      <AccessibilityRegressionSection canManage={canManage} />
    </div>
  );
}
